import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Space,
  Avatar,
  Divider,
  Timeline,
  Progress,
  Statistic,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  List,
  message,
  Spin,
  Tabs,
  Empty,
  InputNumber,
  Alert,
  App,
  Tooltip,
  Drawer,
  Descriptions,
  Badge,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  FileTextOutlined,
  PlusOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  DownloadOutlined,
  FileOutlined,
  DeleteOutlined,
  WhatsAppOutlined,
  SendOutlined,
  PhoneFilled,
  AudioOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  CustomerServiceOutlined,
  ClockCircleFilled,
  InfoCircleOutlined,
  FilePdfOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import useLeadStore from "../../store/leadStore";
import useAuthStore from "../../store/authStore";
import useSettingsStore from "../../store/settingsStore";
import TasksList from "../TasksList";
import apiService from "../../services/apiService";
import whatsappSettingsService from "../../services/whatsappSettingsService";
import dayjs from "dayjs";
import { v4 as uuidv4 } from 'uuid'; // Import uuid for local message IDs
import callLogService from '../../services/callLogService';
import callSettingsService from '../../services/callSettingsService';
import EmailCompose from "./EmailCompose";
import EmailHistory from "./EmailHistory";
import MeetingDrawer from "./MeetingDrawer";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Check if WhatsApp feature is enabled via environment variable
const isWhatsAppEnabled = process.env.REACT_APP_WHATSAPP_ENABLED === 'true';

// Check if AI Calling feature is enabled via environment variable
const isAICallingEnabled = process.env.REACT_APP_AI_CALLING_ENABLED === 'true';

const DEFAULT_FROM_NUMBER = process.env.REACT_APP_PLIVO_SOURCE_NUMBER || process.env.REACT_APP_PLIVO_CALLER_ID || '';

const LeadDetailView = () => {
  const { message: messageApi } = App.useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  // State for WhatsApp channel ID fetched from database
  const [whatsappChannelId, setWhatsappChannelId] = useState('');
  const formatCurrency = useSettingsStore((s) => s.formatCurrency);
  const currencySymbol = useSettingsStore((s) => s.currencySymbol);
  const {
    currentLead,
    fetchLeadById,
    updateLead,
    addNote,
    updateNote,
    deleteNote,
    addActivity,
    addRequirement,
    updateRequirement,
    deleteRequirement,
    addMeeting,
    updateMeeting,
    deleteMeeting,
    addCall,
    updateCall,
    deleteCall,
    addDocument,
    addTask,
    updateTask,
    deleteTask,
    uploadDocument,
    deleteDocument,
    convertToDeal,
  } = useLeadStore();

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [documentModalVisible, setDocumentModalVisible] = useState(false);
  const [activityModalVisible, setActivityModalVisible] = useState(false);
  const [requirementModalVisible, setRequirementModalVisible] = useState(false);
  const [meetingDrawerVisible, setMeetingDrawerVisible] = useState(false);
  const [parentMeeting, setParentMeeting] = useState(null);
  const [callModalVisible, setCallModalVisible] = useState(false);
  const [convertToDealModalVisible, setConvertToDealModalVisible] =
    useState(false);
  const [assignLeadModalVisible, setAssignLeadModalVisible] = useState(false);
  const [proposalDrawerVisible, setProposalDrawerVisible] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [activeTab, setActiveTab] = useState("requirements");
  const [activityType, setActivityType] = useState("note");
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingProposal, setIsGeneratingProposal] = useState(false);
  const [presalesNotesDraft, setPresalesNotesDraft] = useState("");
  const [savingPresalesNotes, setSavingPresalesNotes] = useState(false);
  const [form] = Form.useForm();
  const [taskForm] = Form.useForm();
  const [noteForm] = Form.useForm();
  const [documentForm] = Form.useForm();
  const [convertForm] = Form.useForm();
  const [activityForm] = Form.useForm();
  const [requirementForm] = Form.useForm();
  const [meetingForm] = Form.useForm();
  const [callForm] = Form.useForm();
  const [assignForm] = Form.useForm();
  const [proposalForm] = Form.useForm();
  const isConverted = currentLead?.convertedToDeal;

  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const chatContainerRef = useRef(null);
  const [isCalling, setIsCalling] = useState(false); // New state for AI calling
  const [transcripts, setTranscripts] = useState([]); // New state for transcripts
  const [isTranscriptsLoading, setIsTranscriptsLoading] = useState(false); // New state for transcripts loading
  const [uploadingFiles, setUploadingFiles] = useState([]); // Track uploading files
  const [whatsappUnavailable, setWhatsappUnavailable] = useState(false);
  const [manualCallModalVisible, setManualCallModalVisible] = useState(false);
  const [manualCallForm] = Form.useForm();
  const [callLogs, setCallLogs] = useState([]);
  const [isLoadingCallLogs, setIsLoadingCallLogs] = useState(false);
  const [emailRefreshTrigger, setEmailRefreshTrigger] = useState(0); // Trigger for email list refresh
  
  // Call number modal states
  const [callNumberModalVisible, setCallNumberModalVisible] = useState(false);
  const [isInitiatingCall, setIsInitiatingCall] = useState(false);
  
  // Call settings state
  const [callSettings, setCallSettings] = useState(null);
  const [availablePhoneNumbers, setAvailablePhoneNumbers] = useState([]);
  const [isLoadingCallSettings, setIsLoadingCallSettings] = useState(false);
  
  // Phone selection modal state
  const [phoneSelectionModalVisible, setPhoneSelectionModalVisible] = useState(false);
  const [selectedCountryIndex, setSelectedCountryIndex] = useState(0);
  const [selectedPhoneIndex, setSelectedPhoneIndex] = useState(0);
  const [selectedCredentials, setSelectedCredentials] = useState(null);
  
  // AI Call phone selection modal state
  const [aiCallPhoneModalVisible, setAICallPhoneModalVisible] = useState(false);
  const [aiCallSelectedCountryIndex, setAICallSelectedCountryIndex] = useState(0);
  const [aiCallSelectedPhoneIndex, setAICallSelectedPhoneIndex] = useState(0);
  const [isInitiatingAICall, setIsInitiatingAICall] = useState(false);

  const [activeCallUUID, setActiveCallUUID] = useState(null);

  // Stop ringing audio if present
  const stopRingingAudio = () => {
    const ringingAudio = document.getElementById('plivo-ringing-audio');
    if (ringingAudio) {
      ringingAudio.pause();
      ringingAudio.currentTime = 0;
    }
  };

  // Fetch call logs when tab changes to 'calllogs'
  useEffect(() => {
    const fetchCallLogs = async () => {
      if (activeTab === 'calllogs' && currentLead?.phone) {
        setIsLoadingCallLogs(true);
        try {
          const response = await callLogService.getCallLogs(currentLead.phone);
          if (response.success) {
            setCallLogs(response.data);
          } else {
            messageApi.error('Failed to fetch call logs');
          }
        } catch (error) {
          console.error('Error fetching call logs:', error);
          messageApi.error('Failed to fetch call logs');
        } finally {
          setIsLoadingCallLogs(false);
        }
      }
    };
    fetchCallLogs();
  }, [activeTab, currentLead?.phone]);
  

  useEffect(() => {
    const loadLeadData = async () => {
      if (id) {
        setIsLoading(true); // Set loading to true before fetching
        await fetchLeadById(id);
        loadTeamMembers();
        setIsLoading(false); // Set loading to false after fetching
      }
    };
    loadLeadData();
  }, [id, fetchLeadById]);

  useEffect(() => {
    setPresalesNotesDraft(currentLead?.presalesNotes || "");
  }, [currentLead?._id, currentLead?.presalesNotes]);

  // Fetch call settings from backend
  useEffect(() => {
    const fetchCallSettings = async () => {
      try {
        console.log('[fetchCallSettings] Starting to fetch call settings');
        setIsLoadingCallSettings(true);
        const response = await callSettingsService.getCallSettings();
        console.log('[fetchCallSettings] Response:', response);
        if (response.success && response.data) {
          console.log('[fetchCallSettings] Setting call settings:', response.data);
          setCallSettings(response.data);
          // Get available phone numbers for the current provider
          const phoneNumbers = await callSettingsService.getAvailablePhoneNumbers();
          console.log('[fetchCallSettings] Available phone numbers:', phoneNumbers);
          setAvailablePhoneNumbers(phoneNumbers);
        } else {
          console.log('[fetchCallSettings] Response not successful or no data');
        }
      } catch (error) {
        console.error('[fetchCallSettings] Error fetching call settings:', error);
      } finally {
        setIsLoadingCallSettings(false);
      }
    };

    fetchCallSettings();
  }, []);

  // Fetch WhatsApp settings from database
  useEffect(() => {
    const fetchWhatsAppSettings = async () => {
      try {
        console.log('[fetchWhatsAppSettings] Starting to fetch WhatsApp settings');
        const response = await whatsappSettingsService.getWhatsappSettings();
        if (response.success && response.data) {
          console.log('[fetchWhatsAppSettings] WhatsApp settings:', response.data);
          setWhatsappChannelId(response.data.whatsappChannelId || '');
        } else {
          console.log('[fetchWhatsAppSettings] No WhatsApp settings found');
          setWhatsappChannelId('');
        }
      } catch (error) {
        console.error('[fetchWhatsAppSettings] Error fetching WhatsApp settings:', error);
        setWhatsappChannelId('');
      }
    };

    if (isWhatsAppEnabled) {
      fetchWhatsAppSettings();
    }
  }, []);

  // Initialize Plivo Browser SDK (WebRTC) once on app mount
  useEffect(() => {
    const initializePlivo = () => {
      if (!window.Plivo) {
        console.warn('[Plivo] Browser SDK not loaded yet.');
        return;
      }

      try {
        const options = {
          debug: 'INFO',
          permOnClick: true,
          enableQualityTracking: 'all',
          closeProtection: true,
        };
        // Initialize Browser SDK v2 (without logging in yet)
        const plivoBrowserSdk = new window.Plivo(options);

        // Register events on the client
        plivoBrowserSdk.client.on('onLogin', () => console.log('[Plivo] Logged in'));
        plivoBrowserSdk.client.on('onLoginFailed', (err) => console.error('[Plivo] Login failed:', err));
        plivoBrowserSdk.client.on('onLogout', () => console.log('[Plivo] Logged out'));

        plivoBrowserSdk.client.on('onIncomingCall', (callerID, extraHeaders, callInfo, callerName) => {
          console.log('[Plivo] Incoming call:', { callerID, callerName, callInfo, extraHeaders });
          try {
            // Answer the most recent ringing call or use callInfo.callUUID explicitly
            plivoBrowserSdk.client.answer(callInfo?.callUUID);
          } catch (e) {
            console.error('[Plivo] Failed to answer call:', e);
          }
        });
        plivoBrowserSdk.client.on('onCallAnswered', (callInfo) => {
          console.log('[Plivo] Call answered:', callInfo);
          messageApi.destroy(); // Clear the "Calling..." message
          messageApi.success('Call connected');
          // Keep isCalling as true so End Call button remains visible
        });
        
        plivoBrowserSdk.client.on('onCallConnected', (callInfo) => {
          console.log('[Plivo] Call connected:', callInfo);
          messageApi.destroy(); // Clear the "Calling..." message
          // Keep isCalling as true so End Call button remains visible
        });
        
        plivoBrowserSdk.client.on('onCallTerminated', (hangupInfo, callInfo) => {
          console.log('[Plivo] Call ended:', { hangupInfo, callInfo });
          messageApi.destroy(); // Clear any existing messages
          messageApi.info('Call ended');
          setIsCalling(false);
          setActiveCallUUID(null);
        });

        // Handle media failures (can happen when remote party hangs up)
        plivoBrowserSdk.client.on('onMediaError', (err) => {
          console.error('[Plivo] Media error:', err);
          messageApi.destroy();
          messageApi.error('Call disconnected due to media error');
          setIsCalling(false);
          setActiveCallUUID(null);
        });

        // Handle call failures
        plivoBrowserSdk.client.on('onCallFailed', (reason, callInfo) => {
          console.error('[Plivo] Call failed:', { reason, callInfo });
          messageApi.destroy();
          messageApi.error('Call failed: ' + reason);
          setIsCalling(false);
          setActiveCallUUID(null);
        });
        
        // Handle call rejection from remote party
        plivoBrowserSdk.client.on('onCallRejected', (callInfo) => {
          stopRingingAudio();
          console.log('[Plivo] Call rejected by remote party:', callInfo);
          messageApi.destroy();
          messageApi.info('Call rejected by other party');
          setIsCalling(false);
          setActiveCallUUID(null);
        });

        // Handle remote disconnect/hangup specifically
        plivoBrowserSdk.client.on('onRemoteDisconnect', (callInfo) => {
          stopRingingAudio();
          console.log('[Plivo] Remote party disconnected:', callInfo);
          messageApi.destroy();
          messageApi.info('Other party ended the call');
          setIsCalling(false);
          setActiveCallUUID(null);
        });

        // Expose globally for debugging/usage in other components if needed
        window._plivo = plivoBrowserSdk;
      } catch (e) {
        console.error('[Plivo] Initialization error:', e);
      }
    };

    initializePlivo();

    // Ensure any stray ringing audio is stopped on mount
    stopRingingAudio();
  }, [messageApi]);

  // Ensure Plivo is logged in with SIP credentials for the currently selected country
  useEffect(() => {
    const loginForSelectedCountry = () => {
      if (!window._plivo?.client) {
        return;
      }

      const countries = callSettings?.plivoSettings?.countries;
      if (!countries || countries.length === 0) {
        return;
      }

      const safeIndex = Math.min(selectedCountryIndex, countries.length - 1);
      const country = countries[safeIndex];

      if (!country?.sipUser || !country?.sipPassword) {
        console.warn('[Plivo] Missing SIP credentials for selected country', country?.countryName);
        return;
      }

      try {
        const client = window._plivo.client;

        // If already registered as this user, no need to re-login
        if (typeof client.getContactUri === 'function') {
          const uri = client.getContactUri();
          if (uri && uri.includes(country.sipUser)) {
            return;
          }
        }

        // If already registered with some user, log out first (if API supports it)
        if (typeof client.isRegistered === 'function' && client.isRegistered()) {
          client.logout();
        }

        console.log('[Plivo] Logging in as', country.sipUser, 'for country', country.countryName);
        client.login(country.sipUser, country.sipPassword);
      } catch (error) {
        console.error('[Plivo] Error logging in with selected country credentials:', error);
      }
    };

    loginForSelectedCountry();
  }, [callSettings, selectedCountryIndex]);

  const handleEndCall = () => {
    stopRingingAudio();
    if (window._plivo?.client && activeCallUUID) {
      try {
        window._plivo.client.hangup();
        setActiveCallUUID(null);
        setIsCalling(false);
        messageApi.destroy(); // Clear any existing messages
        messageApi.info("Call ended");
      } catch (error) {
        console.error("Error ending call:", error);
        messageApi.error("Failed to end call");
      }
    }
  };


  const handleStartManualCall = async (values) => {
    const numberToCall = currentLead?.phone || values.phoneNumber;
    if (!numberToCall) { 
      messageApi.error("No phone number available to call.");
      return;
    }

    // Check if we have call settings configured
    if (!callSettings || !callSettings.plivoSettings || callSettings.plivoSettings.countries.length === 0) {
      messageApi.warning("No phone numbers configured in call settings. Please configure call settings first.");
      return;
    }

    // Open phone selection modal
    setPhoneSelectionModalVisible(true);
  };

  const handleConfirmPhoneSelection = async () => {
    const numberToCall = currentLead?.phone || manualCallForm.getFieldValue('phoneNumber');
    if (!numberToCall) { 
      messageApi.error("No phone number available to call.");
      return;
    }

    if (!window._plivo?.client) {
      messageApi.error("Plivo client not initialized.");
      console.error("Plivo client not initialized.");
      return;
    }

    try {
      setIsCalling(true);
      messageApi.open({ type: "loading", content: "Calling...", duration: 0 });

      // Get selected country and phone
      const selectedCountry = callSettings.plivoSettings.countries[selectedCountryIndex];
      const selectedPhone = selectedCountry.phoneNumbers[selectedPhoneIndex];

      // Normalize destination number: strip all non-digits and format as +<digits>
      const digitsOnly = String(numberToCall).replace(/\D/g, '');
      if (!digitsOnly) {
        messageApi.error("Invalid phone number. Please check the number and try again.");
        setIsCalling(false);
        messageApi.destroy();
        return;
      }
      const formattedNumber = `+${digitsOnly}`;

      console.log(`[Call] Making call from ${selectedPhone} (${selectedCountry.countryName}) to ${formattedNumber}`);
      
      // Make call
      const callUUID = window._plivo.client.call(formattedNumber);
      setActiveCallUUID(callUUID);

      // Close modal
      setPhoneSelectionModalVisible(false);

    } catch (error) {
      console.error("Error during browser call:", error);
      messageApi.error("Failed to initiate browser call.");
      messageApi.destroy();
      setIsCalling(false);
    }
  };

  // Handle mute/unmute during call
  const handleToggleMute = () => {
    try {
      if (!window._plivo?.client) {
        messageApi.error("Plivo client not initialized.");
        return;
      }

      if (!activeCallUUID) {
        messageApi.warning("No active call.");
        return;
      }

      const isMuted = window._plivo.client.isMuted(activeCallUUID);
      
      if (isMuted) {
        window._plivo.client.unmute(activeCallUUID);
        console.log('[Call] Unmuted');
        messageApi.info('Unmuted');
      } else {
        window._plivo.client.mute(activeCallUUID);
        console.log('[Call] Muted');
        messageApi.info('Muted');
      }
    } catch (error) {
      console.error('[Call] Error toggling mute:', error);
      messageApi.error('Failed to toggle mute');
    }
  };

  // Handle speaker toggle during call
  const handleToggleSpeaker = () => {
    try {
      if (!window._plivo?.client) {
        messageApi.error("Plivo client not initialized.");
        return;
      }

      if (!activeCallUUID) {
        messageApi.warning("No active call.");
        return;
      }

      // Toggle speaker (this depends on Plivo SDK capabilities)
      // Note: Plivo SDK may not have direct speaker toggle, this is a placeholder
      console.log('[Call] Speaker toggle requested');
      messageApi.info('Speaker toggled');
    } catch (error) {
      console.error('[Call] Error toggling speaker:', error);
      messageApi.error('Failed to toggle speaker');
    }
  };

  const handleStartAICallCampaign = async () => {
    if (!currentLead?._id) {
      messageApi.error("Lead ID is missing. Cannot start AI call campaign.");
      return;
    }
    
    // Validate call settings are configured
    if (!callSettings?.plivoSettings?.countries || callSettings.plivoSettings.countries.length === 0) {
      messageApi.error("Call settings are not configured. Please configure call settings first.");
      return;
    }
    
    // Open phone selection modal
    setAICallSelectedCountryIndex(0);
    setAICallSelectedPhoneIndex(0);
    setAICallPhoneModalVisible(true);
  };

  const handleConfirmAICallPhoneSelection = async () => {
    if (!currentLead?._id) {
      messageApi.error("Lead ID is missing. Cannot start AI call campaign.");
      return;
    }

    const selectedCountry = callSettings?.plivoSettings?.countries?.[aiCallSelectedCountryIndex];
    if (!selectedCountry) {
      messageApi.error("Invalid country selection.");
      return;
    }

    const selectedPhoneNumber = selectedCountry.phoneNumbers?.[aiCallSelectedPhoneIndex];
    if (!selectedPhoneNumber) {
      messageApi.error("Invalid phone number selection.");
      return;
    }

    // Normalize selected phone number before sending to backend
    const aiCallDigitsOnly = String(selectedPhoneNumber).replace(/\D/g, '');
    if (!aiCallDigitsOnly) {
      messageApi.error("Invalid phone number. Please check the number and try again.");
      return;
    }

    setIsInitiatingAICall(true);
    try {
      const payload = {
        phoneNumber: aiCallDigitsOnly,
        countryCode: selectedCountry.countryCode,
      };

      const response = await apiService.post(
        `/leads/${currentLead._id}/start-aicall-campaign`,
        payload
      );

      if (response.data.code === "AICALL_CAMPAIGN_STARTED") {
        messageApi.success(response.data.message);
        setAICallPhoneModalVisible(false);
        // Optionally, update lead status or add an activity for the call
        fetchLeadById(id);
      } else {
        messageApi.error(response.data.message || "Failed to start AI call campaign.");
      }
    } catch (error) {
      console.error("Error starting AI call campaign:", error);
      messageApi.error(error.response?.data?.message || "Failed to start AI call campaign.");
    } finally {
      setIsInitiatingAICall(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      // Create a temporary anchor element to download the file
      const link = document.createElement('a');
      link.href = doc.url;
      link.target = '_blank';
      link.download = doc.name;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Downloading ${doc.name}`);
    } catch (error) {
      console.error('Download error:', error);
      message.error('Failed to download file');
    }
  };

  useEffect(() => {
    const hasChannel = currentLead?.channelId || whatsappChannelId;
    if (activeTab === 'whatsapp' && (hasChannel || currentLead?.phone)) {
      fetchChatMessages();
    } else if (activeTab === 'aicalling' && currentLead?.campaignIds?.length > 0) {
      fetchTranscripts();
    }
  }, [activeTab, currentLead?.channelId, currentLead?.phone, currentLead?.campaignIds, whatsappChannelId]);

  // Poll for inbound WhatsApp messages while the WhatsApp tab is open. The
  // server stores inbound messages asynchronously when WaSenderAPI hits our
  // webhook, so the UI needs to refresh to see them.
  useEffect(() => {
    if (activeTab !== 'whatsapp') return undefined;
    if (whatsappUnavailable) return undefined;
    const hasChannel = currentLead?.channelId || whatsappChannelId;
    if (!hasChannel && !currentLead?.phone) return undefined;

    const intervalId = setInterval(() => {
      fetchChatMessages();
    }, 8000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, whatsappUnavailable, currentLead?.channelId, currentLead?.phone, whatsappChannelId]);

  // Auto-scroll to bottom when messages update and WhatsApp tab is active
  useEffect(() => {
    if (activeTab === 'whatsapp' && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages, activeTab]);

  const fetchTranscripts = async () => {
    if (!currentLead?.campaignIds || currentLead.campaignIds.length === 0) {
      setTranscripts([]);
      return;
    }

    setIsTranscriptsLoading(true);
    try {
      const response = await apiService.post('/pitchbot/transcripts', {
        ids: currentLead.campaignIds,
      });
      if (response.status === 200) {
        setTranscripts(response.data);
      } else {
        messageApi.error(response.data.message || "Failed to fetch transcripts.");
      }
    } catch (error) {
      console.error("Error fetching transcripts:", error);
      messageApi.error(error.response?.data?.message || "Failed to fetch transcripts.");
    } finally {
      setIsTranscriptsLoading(false);
    }
  };

  const fetchChatMessages = async () => {
    const resolvedChannelId = currentLead?.channelId || whatsappChannelId;
    if (!resolvedChannelId && !currentLead?.phone) {
      messageApi.warning("Lead does not have a channel ID or phone number for WhatsApp chat.");
      return;
    }
    setIsChatLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (resolvedChannelId) {
        queryParams.append('channelId', resolvedChannelId);
      }
      if (currentLead.phone) {
        queryParams.append('phoneNumber', currentLead.phone);
      }
      const response = await apiService.get(`/chat/messages?${queryParams.toString()}`);
      if (response.data.success) {
        setWhatsappUnavailable(false);
        setChatMessages(response.data.data.messages);
      } else {
        if (response.data.code === "WHATSAPP_CONFIG_MISSING" || response.data.code === "WHATSAPP_PROVIDER_UNAVAILABLE") {
          setWhatsappUnavailable(true);
        }
        messageApi.error(response.data.message || "Failed to fetch chat messages.");
      }
    } catch (error) {
      console.error("Failed to fetch chat messages:", error);
      const serverCode = error?.response?.data?.code;
      if (serverCode === "WHATSAPP_CONFIG_MISSING" || serverCode === "WHATSAPP_PROVIDER_UNAVAILABLE") {
        setWhatsappUnavailable(true);
        messageApi.error(error.response?.data?.message || "WhatsApp integration is unavailable.");
      } else {
        messageApi.error("Failed to fetch chat messages.");
      }
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleSendChatMessage = async () => {
    if (!chatInput.trim()) {
      return;
    }
    const resolvedChannelId = currentLead?.channelId || whatsappChannelId;
    if (!resolvedChannelId || !currentLead?.phone) {
      messageApi.error("Cannot send message: Missing channel ID or recipient phone.");
      return;
    }
    if (whatsappUnavailable) {
      messageApi.error("WhatsApp integration is currently unavailable.");
      return;
    }

    const newMessage = {
      _id: uuidv4(), // Temporary ID for optimistic update
      entity: 'bot', // Assuming messages sent from CRM are bot/agent responses
      message: chatInput,
      timestamp: new Date().toISOString(),
      channelId: resolvedChannelId,
      contactId: currentLead.contactId, // Assuming contactId is available on currentLead
      recipientPhone: currentLead.phone,
      conversationId: currentLead.conversationId, // Assuming conversationId is available
      accountId: currentLead.accountId, // Assuming accountId is available
      contactName: `${currentLead.firstName} ${currentLead.lastName}`,
      channelNumber: currentLead.channelNumber, // Assuming channelNumber is available
      whatsappType: 'text',
    };

    // Optimistic update
    setChatMessages((prevMessages) => [...prevMessages, newMessage]);
    setChatInput('');

    try {
      const response = await apiService.post('/chat/whatsapp/send', {
        channelId: resolvedChannelId,
        contactId: currentLead.contactId,
        recipientPhone: currentLead.phone,
        message: newMessage.message,
        conversationId: currentLead.conversationId,
        accountId: currentLead.accountId,
        contactName: `${currentLead.firstName} ${currentLead.lastName}`,
        channelNumber: currentLead.channelNumber,
      });

      if (response.data.success) {
        messageApi.success("Message sent successfully!");
        // Replace optimistic message with actual message from backend if needed, or just refetch
        fetchChatMessages();
      } else {
        if (response.data.code === "WHATSAPP_CONFIG_MISSING" || response.data.code === "WHATSAPP_PROVIDER_UNAVAILABLE") {
          setWhatsappUnavailable(true);
        }
        messageApi.error(response.data.message || "Failed to send message.");
        // Revert optimistic update on error
        setChatMessages((prevMessages) => prevMessages.filter(msg => msg._id !== newMessage._id));
      }
    } catch (error) {
      // Extract detailed error info from server if available
      const serverMessage = error?.response?.data?.message;
      const upstreamStatus = error?.response?.data?.upstreamStatus;
      const serverCode = error?.response?.data?.code;
      const baseMsg = serverMessage || error?.message || "Failed to send message.";
      console.error("Error sending chat message:", {
        error,
        serverMessage,
        upstreamStatus,
        response: error?.response?.data,
      });
      if (serverCode === "WHATSAPP_CONFIG_MISSING" || serverCode === "WHATSAPP_PROVIDER_UNAVAILABLE") {
        setWhatsappUnavailable(true);
      }
      messageApi.error(baseMsg);
      // Revert optimistic update on error
      setChatMessages((prevMessages) => prevMessages.filter((msg) => msg._id !== newMessage._id));
    }
  };

  const loadTeamMembers = async () => {
    setLoadingTeamMembers(true);
    try {
      const response = await apiService.get("/users/team-members");

      if (response.data.success) {
        setTeamMembers(response.data.data.teamMembers);
      }
    } catch (error) {
      console.error("Failed to load team members:", error);
      if (error.response) {
        console.error(
          "Error response:",
          error.response.status,
          error.response.data
        );
      }
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  const handleAddNote = async (values) => {
    const result = await addNote(id, values.content);
    if (result.success) {
      message.success("Note added successfully");
      setNoteModalVisible(false);
      noteForm.resetFields();
      await fetchLeadById(id); // Refresh data
    } else {
      message.error(result.error || "Failed to add note");
    }
  };

  const handleAddActivity = async (values) => {
    const result = await addActivity(id, {
      type: activityType,
      description: values.description,
      date: new Date(),
    });
    if (result.success) {
      message.success("Activity added successfully");
      setActivityModalVisible(false);
      activityForm.resetFields();
      fetchLeadById(id); // Refresh lead data to update activity timeline
    } else {
      message.error(result.error || "Failed to add activity");
    }
  };

  const handleAddRequirement = async (values) => {
    const result = await addRequirement(id, values);
    if (result.success) {
      message.success("Requirement added successfully");
      setRequirementModalVisible(false);
      setEditingItem(null);
      requirementForm.resetFields();
      fetchLeadById(id); // Refresh lead data
    } else {
      message.error(result.error || "Failed to add requirement");
    }
  };

  const handleUpdateRequirement = async (values) => {
    const result = await updateRequirement(id, editingItem._id, values);
    if (result.success) {
      setRequirementModalVisible(false);
      requirementForm.resetFields();
      setEditingItem(null);
      message.success("Requirement updated successfully");
      fetchLeadById(id); // Refresh lead data
    } else {
      message.error(result.error || "Failed to update requirement");
    }
  };

  const handleDeleteRequirement = async (requirementId) => {
    const result = await deleteRequirement(id, requirementId);
    if (result.success) {
      message.success("Requirement deleted successfully");
      fetchLeadById(id); // Refresh lead data
    } else {
      message.error(result.error || "Failed to delete requirement");
    }
  };

  const handleAddMeeting = async (values, followUpData = null) => {
    try {
      const result = await addMeeting(id, values, followUpData);
      if (result.success) {
        message.success("Meeting scheduled successfully");
        setMeetingDrawerVisible(false);
        setEditingItem(null);
        setParentMeeting(null);
        fetchLeadById(id); // Refresh lead data to update activity timeline
      } else {
        message.error(result.error || "Failed to add meeting");
      }
    } catch (error) {
      console.error("Error adding meeting:", error);
      message.error(error.message || "Failed to add meeting");
    }
  };

  const handleUpdateMeeting = async (values) => {
    try {
      const result = await updateMeeting(id, editingItem._id, values);
      if (result.success) {
        setMeetingDrawerVisible(false);
        setEditingItem(null);
        message.success("Meeting updated successfully");
        fetchLeadById(id); // Refresh lead data to update activity timeline
      } else {
        message.error(result.error || "Failed to update meeting");
      }
    } catch (error) {
      console.error("Error updating meeting:", error);
      message.error(error.message || "Failed to update meeting");
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    const result = await deleteMeeting(id, meetingId);
    if (result.success) {
      message.success("Meeting deleted successfully");
      fetchLeadById(id); // Refresh lead data to update activity timeline
    } else {
      message.error(result.error || "Failed to delete meeting");
    }
  };

  const handleAddCall = async (values) => {
    try {
      // Format the data for the API
      const callData = {
        type: values.type,
        subject: values.subject,
        notes: values.notes,
        duration: values.duration || 0,
        outcome: values.outcome || "successful",
      };

      // Only include scheduledAt if user provided it
      if (values.scheduledAt) {
        callData.scheduledAt = values.scheduledAt.toISOString();
      }

      const result = await addCall(id, callData);

      if (result.success) {
        setCallModalVisible(false);
        callForm.resetFields();
        setEditingItem(null);
        messageApi.success("Call logged successfully");
        // Refresh the lead data to show the new call
        await fetchLeadById(id);
      } else {
        messageApi.error(result.error || "Failed to log call");
      }
    } catch (error) {
      console.error("Error in handleAddCall:", error);
      messageApi.error("Failed to log call");
    }
  };

  const handleUpdateCall = async (values) => {
    try {
      const callData = {
        type: values.type,
        subject: values.subject,
        notes: values.notes,
        duration: values.duration || 0,
        outcome: values.outcome || "successful",
      };

      // Only include scheduledAt if provided or already exists
      if (values.scheduledAt) {
        callData.scheduledAt = values.scheduledAt.toISOString();
      } else if (editingItem.scheduledAt) {
        callData.scheduledAt = editingItem.scheduledAt;
      }

      const result = await updateCall(id, editingItem._id, callData);

      if (result.success) {
        setCallModalVisible(false);
        callForm.resetFields();
        setEditingItem(null);
        messageApi.success("Call updated successfully");
        await fetchLeadById(id);
      } else {
        messageApi.error(result.error || "Failed to update call");
      }
    } catch (error) {
      console.error("Error in handleUpdateCall:", error);
      messageApi.error("Failed to update call");
    }
  };

  const handleDeleteCall = async (callId) => {
    try {
      const result = await deleteCall(id, callId);
      if (result.success) {
        messageApi.success("Call deleted successfully");
        await fetchLeadById(id);
      } else {
        messageApi.error(result.error || "Failed to delete call");
      }
    } catch (error) {
      console.error("Error in handleDeleteCall:", error);
      messageApi.error("Failed to delete call");
    }
  };

  const handleStartCall = () => {
    console.log('[handleStartCall] Called');
    console.log('[handleStartCall] currentLead?.phone:', currentLead?.phone);
    console.log('[handleStartCall] callSettings:', callSettings);
    
    if (!currentLead?.phone) {
      messageApi.error("Contact phone number is not available");
      return;
    }

    // Check if we have call settings configured
    if (!callSettings || !callSettings.plivoSettings || callSettings.plivoSettings.countries.length === 0) {
      console.log('[handleStartCall] No call settings configured');
      messageApi.warning("No phone numbers configured in call settings. Please configure call settings first.");
      return;
    }

    // Open phone selection modal
    console.log('[handleStartCall] Opening phone selection modal');
    setPhoneSelectionModalVisible(true);
  };

  const handleCallNumberConfirm = async (callData) => {
    try {
      setIsInitiatingCall(true);
      const response = await apiService.post(`/leads/${id}/initiate-call`, {
        fromNumber: callData.fromNumber,
        toNumber: callData.toNumber,
        countryCode: callData.countryCode,
        provider: callData.provider
      });

      if (response.data.code === 'CALL_INITIATED') {
        messageApi.success('Call initiated successfully');
        setCallNumberModalVisible(false);
        await fetchLeadById(id); // Refresh data
      } else {
        messageApi.error(response.data.message || 'Failed to initiate call');
      }
    } catch (error) {
      console.error('Error initiating call:', error);
      messageApi.error(error.response?.data?.message || 'Failed to initiate call');
    } finally {
      setIsInitiatingCall(false);
    }
  };

  const handleAddDocument = async (values) => {
    const result = await addDocument(id, values);
    if (result.success) {
      setDocumentModalVisible(false);
      message.success("Document added successfully");
    } else {
      message.error(result.error || "Failed to add document");
    }
  };

  const handleDeleteDocument = async (documentId) => {
    const result = await deleteDocument(id, documentId);
    if (result.success) {
      message.success("Document deleted successfully");
    } else {
      message.error(result.error || "Failed to delete document");
    }
  };

  const handleEditItem = (type, item) => {
    if (type === "note") {
      setEditingItem({ type: "note", item });
      noteForm.setFieldsValue({ content: item.content });
      setNoteModalVisible(true);
      return;
    }

    setEditingItem(item);
    if (type === "requirement") {
      requirementForm.setFieldsValue(item);
      setRequirementModalVisible(true);
    } else if (type === "meeting") {
      setMeetingDrawerVisible(true);
    } else if (type === "call") {
      callForm.setFieldsValue({
        ...item,
        scheduledAt: item.scheduledAt ? dayjs(item.scheduledAt) : null,
      });
      setCallModalVisible(true);
    }
  };

  const handleDeleteItem = (type, itemId) => {
    Modal.confirm({
      title: `Delete ${type}?`,
      content: `Are you sure you want to delete this ${type}?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        if (type === "requirement") {
          handleDeleteRequirement(itemId);
        } else if (type === "meeting") {
          handleDeleteMeeting(itemId);
        } else if (type === "call") {
          handleDeleteCall(itemId);
        } else if (type === "document") {
          handleDeleteDocument(itemId);
        } else if (type === "note") {
          try {
            const result = await deleteNote(id, itemId);
            if (result.success) {
              message.success('Note deleted successfully');
              await fetchLeadById(id); // Refresh lead data
            } else {
              message.error(result.error || 'Failed to delete note');
            }
          } catch (error) {
            message.error('Failed to delete note');
            console.error('Error deleting note:', error);
          }
        }
      },
    });
  };

  const handleUpdateNote = async (values) => {
    if (editingItem?.type === "note") {
      const result = await updateNote(id, editingItem.item._id, values.content);
      if (result.success) {
        message.success("Note updated successfully");
        setNoteModalVisible(false);
        setEditingItem(null);
        noteForm.resetFields();
        await fetchLeadById(id);
      } else {
        message.error(result.error || "Failed to update note");
      }
    } else {
      handleAddNote(values);
    }
  };

  const handleConvertToDeal = async (values) => {
    // Transform the form values to match backend expectations
    setIsLoading(true);
    try {
      const result = await convertToDeal(id, values);
      if (result.success) {
        message.success("Lead converted to deal successfully");
        setConvertToDealModalVisible(false);
        convertForm.resetFields();
        navigate("/deals");
      } else {
        message.error(result.error || "Failed to convert lead to deal");
      }
    } catch (error) {
      message.error("Failed to convert lead to deal");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePresalesNotes = async () => {
    setSavingPresalesNotes(true);
    try {
      const result = await updateLead(id, { presalesNotes: presalesNotesDraft });
      if (result.success) {
        message.success("Presales notes saved");
        fetchLeadById(id);
      } else {
        message.error(result.error || "Failed to save presales notes");
      }
    } finally {
      setSavingPresalesNotes(false);
    }
  };

  const handleAssignLead = async (values) => {
    try {
      const result = await updateLead(id, { assignedTo: values.assignedTo });
      if (result.success) {
        message.success("Lead assignment updated successfully");
        setAssignLeadModalVisible(false);
        assignForm.resetFields();
        fetchLeadById(id); // Refresh lead data
      } else {
        message.error(result.error || "Failed to update lead assignment");
      }
    } catch (error) {
      message.error("Failed to update lead assignment");
    }
  };

  const [proposalPreviewVisible, setProposalPreviewVisible] = useState(false);
  const [proposalPreviewData, setProposalPreviewData] = useState(null);
  const [editedProposalContent, setEditedProposalContent] = useState('');
  const [editedCompanyInfo, setEditedCompanyInfo] = useState(null);

  const handleGenerateProposal = async (values) => {
    try {
      setIsGeneratingProposal(true);
      
      // Call API to generate proposal preview with extended timeout for AI processing
      const response = await apiService.post(`/leads/${id}/generate-proposal-preview`, values, {
        timeout: 60000 // 60 seconds for AI generation
      });
      
      if (response.data.success) {
        // Store preview data and show preview modal
        setProposalPreviewData({
          ...response.data.data,
          originalFormValues: values
        });
        setEditedProposalContent(response.data.data.proposalContent);
        setEditedCompanyInfo(response.data.data.companyInfo);
        setProposalPreviewVisible(true);
        setProposalDrawerVisible(false); // Close the form drawer
        messageApi.success("Proposal preview generated! Review and edit before creating PDF.");
      } else {
        messageApi.error(response.data.message || "Failed to generate proposal preview");
      }
    } catch (error) {
      console.error("Error generating proposal preview:", error);
      messageApi.error(error.response?.data?.message || "Failed to generate proposal preview");
    } finally {
      setIsGeneratingProposal(false);
    }
  };

  const handleConfirmProposal = async () => {
    try {
      setIsGeneratingProposal(true);
      
      // Call API to generate final PDF with edited content
      const response = await apiService.post(`/leads/${id}/generate-proposal`, {
        ...proposalPreviewData.originalFormValues,
        proposalContent: editedProposalContent,
        companyInfo: editedCompanyInfo
      }, {
        timeout: 60000
      });
      
      if (response.data.success) {
        messageApi.success("Proposal PDF generated successfully!");
        
        // Download the PDF
        const base64Data = response.data.data.pdfBase64;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });
        
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Proposal_${currentLead?.company || 'Lead'}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        // Close preview modal and reset
        setProposalPreviewVisible(false);
        setProposalPreviewData(null);
        proposalForm.resetFields();
      } else {
        messageApi.error(response.data.message || "Failed to generate proposal PDF");
      }
    } catch (error) {
      console.error("Error generating proposal PDF:", error);
      messageApi.error(error.response?.data?.message || "Failed to generate proposal PDF");
    } finally {
      setIsGeneratingProposal(false);
    }
  };

  const handleAddTask = () => {
    setEditingTask(null);
    taskForm.resetFields();
    setTaskModalVisible(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    taskForm.setFieldsValue({
      title: task.title,
      description: task.description,
      priority: task.priority,
      status: task.status,
      dueDate: task.dueDate ? dayjs(task.dueDate) : null,
      notes: task.notes,
    });
    setTaskModalVisible(true);
  };

  const handleDeleteTask = (taskId) => {
    Modal.confirm({
      title: "Delete Task",
      content: "Are you sure you want to delete this task?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        const result = await deleteTask(id, taskId);
        if (result.success) {
          message.success("Task deleted successfully");
          fetchLeadById(id); // Refresh lead data to update tasks list
        } else {
          message.error(result.error || "Failed to delete task");
        }
      },
    });
  };

  const handleSaveTask = async (values) => {
    const taskData = {
      title: values.title,
      description: values.description || "",
      priority: values.priority || "medium",
      status: values.status || "pending",
      dueDate: values.dueDate ? values.dueDate.toISOString() : null,
      notes: values.notes || "",
      assignedTo: values.assignedTo || null,
    };

    let result;
    if (editingTask) {
      result = await updateTask(id, editingTask._id, taskData);
    } else {
      result = await addTask(id, taskData);
    }

    if (result.success) {
      message.success(
        `Task ${editingTask ? "updated" : "created"} successfully`
      );
      setTaskModalVisible(false);
      taskForm.resetFields();
      setEditingTask(null);
      fetchLeadById(id); // Refresh lead data to update tasks list
    } else {
      // Display validation errors if available
      if (result.details && Array.isArray(result.details)) {
        result.details.forEach((detail) => {
          message.error(detail);
        });
      } else {
        message.error(
          result.error || `Failed to ${editingTask ? "update" : "create"} task`
        );
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: "blue",
      contacted: "orange",
      converted: "success",
      lost: "error",
    };
    return colors[status] || "default";
  };

  const priorityMeta = {
    low: { label: "Cold", emoji: "❄️", color: "red" },
    medium: { label: "Warm", emoji: "🌤️", color: "orange" },
    high: { label: "Hot", emoji: "🔥", color: "#237804" },
    urgent: { label: "Superhot", emoji: "🔥🔥", color: "#135200" },
  };

  const getPriorityColor = (priority) => {
    return priorityMeta[priority]?.color || "default";
  };

  const getPriorityLabel = (priority) => {
    return priorityMeta[priority]?.label || priority;
  };

  const getPriorityEmoji = (priority) => {
    return priorityMeta[priority]?.emoji || "";
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (!currentLead) {
    return (
      <div className="flex justify-center items-center h-64">
        <Empty description="Lead not found" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/leads")}
          className="mb-4"
        >
          Back to Leads
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <Title level={2} className="mb-0">
              {currentLead.firstName} {currentLead.lastName}
            </Title>
            <Text className="text-gray-600 dark:text-gray-400">
              {currentLead.jobTitle}{" "}
              {currentLead.company && `at ${currentLead.company}`}
            </Text>
          </div>
          <Space>
            {user?.role === "tenant_admin" && (
              <div className="flex items-center gap-2">
                {currentLead.assignedTo ? (
                  <div className="flex items-center gap-2 px-3 py-1 bg-gray-50 rounded border">
                    <Avatar size="small">
                      {(() => {
                        const assignedToId =
                          currentLead.assignedTo?._id || currentLead.assignedTo;
                        const member = teamMembers.find(
                          (m) => String(m.id) === String(assignedToId)
                        );
                        return (
                          member?.name?.charAt(0)?.toUpperCase() ||
                          currentLead.assignedTo?.firstName
                            ?.charAt(0)
                            ?.toUpperCase() ||
                          "U"
                        );
                      })()}
                    </Avatar>
                    <Text className="text-sm">
                      {(() => {
                        const assignedToId =
                          currentLead.assignedTo?._id || currentLead.assignedTo;
                        const member = teamMembers.find(
                          (m) => String(m.id) === String(assignedToId)
                        );
                        return (
                          member?.name ||
                          (currentLead.assignedTo?.firstName &&
                          currentLead.assignedTo?.lastName
                            ? `${currentLead.assignedTo.firstName} ${currentLead.assignedTo.lastName}`
                            : "Unknown User")
                        );
                      })()}
                    </Text>
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        const assignedToId =
                          currentLead?.assignedTo?._id ||
                          currentLead?.assignedTo;
                        assignForm.setFieldsValue({ assignedTo: assignedToId });
                        setAssignLeadModalVisible(true);
                      }}
                      className="focus-visible:focus"
                      disabled={isConverted}
                    />
                  </div>
                ) : (
                  <Button
                    type="default"
                    icon={<UserOutlined />}
                    onClick={() => {
                      assignForm.setFieldsValue({ assignedTo: null });
                      setAssignLeadModalVisible(true);
                    }}
                    className="focus-visible:focus"
                    disabled={isConverted}
                  >
                    Assign Lead
                  </Button>
                )}
              </div>
            )}
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/leads/${id}/edit`)}
              className="focus-visible:focus"
              disabled={isConverted}
            >
              Edit Lead
            </Button>
            <Button
              type="primary"
              danger
              icon={<CheckCircleOutlined />}
              onClick={() => setConvertToDealModalVisible(true)}
              className="focus-visible:focus"
              disabled={isConverted}
            >
              Convert to Deal
            </Button>
          </Space>
        </div>
      </div>

      {/* 3-Column Layout */}
      <Row gutter={24} className="h-full">
        {/* Column 1: Lead Details (22%) */}
        <Col span={5}>
          <Card className="shadow-soft h-fit bg-white dark:bg-gray-800">
            <div className="text-center mb-4">
              <Avatar
                size={80}
                src={currentLead.profileImageUrl}
                icon={!currentLead.profileImageUrl ? <UserOutlined /> : undefined}
                className="mb-3"
              />
              <Title level={4} className="mb-1">
                {currentLead.firstName} {currentLead.lastName}
              </Title>
              <Text className="text-gray-600 dark:text-gray-400 block mb-3">
                {currentLead.jobTitle}
              </Text>
              <Space>
                <Tag color={getStatusColor(currentLead.status)}>
                  {currentLead.status?.toUpperCase()}
                </Tag>
                <Tag color={getPriorityColor(currentLead.priority)}>
                  {getPriorityEmoji(currentLead.priority)} {getPriorityLabel(currentLead.priority)}
                </Tag>
              </Space>
            </div>

            <Divider />

            <div className="space-y-3">
              <div className="mb-3">
                <Text strong className="block mb-1 text-gray-600 dark:text-gray-400">
                  Email:
                </Text>
                <div className="flex items-center">
                  <MailOutlined className="mr-2 text-gray-400" />
                  <a
                    href={`mailto:${currentLead.email}`}
                    className="text-blue-600 break-all"
                  >
                    {currentLead.email}
                  </a>
                </div>
              </div>

              {currentLead.phone && (
                <div className="mb-3">
                  <Text
                    strong
                    className="block mb-1 text-gray-600 dark:text-gray-400"
                  >
                    Phone:
                  </Text>
                  <div className="flex items-center">
                    <PhoneOutlined className="mr-2 text-gray-400 icon-flip-horizontal " />
                    <a
                      href={`tel:${currentLead.phone}`}
                      className="text-blue-600"
                    >
                      {currentLead.phone}
                    </a>
                  </div>
                </div>
              )}

              {currentLead.company && (
                <div className="mb-3">
                  <Text
                    strong
                    className="block mb-1 text-gray-600 dark:text-gray-400"
                  >
                    Company:
                  </Text>
                  <Text className="break-words">{currentLead.company}</Text>
                </div>
              )}

              {currentLead.source && (
                <div className="mb-3">
                  <Text
                    strong
                    className="block mb-1 text-gray-600 dark:text-gray-400"
                  >
                    Source:
                  </Text>
                  <Text>
                    {currentLead.source.replace("_", " ").toUpperCase()}
                  </Text>
                </div>
              )}

              {currentLead.estimatedValue && (
                <div className="mb-3">
                  <Text
                    strong
                    className="block mb-1 text-gray-600 dark:text-gray-400"
                  >
                    Est. Value:
                  </Text>
                  <Text>{formatCurrency(currentLead.estimatedValue)}</Text>
                </div>
              )}

              {currentLead.expectedCloseDate && (
                <div className="mb-3">
                  <Text
                    strong
                    className="block mb-1 text-gray-600 dark:text-gray-400"
                  >
                    Expected:
                  </Text>
                  <Text>
                    {dayjs(currentLead.expectedCloseDate).format(
                      "MMM DD, YYYY"
                    )}
                  </Text>
                </div>
              )}

              {user?.role === "tenant_admin" && currentLead.assignedTo && (
                <div className="flex items-center">
                  <Text
                    strong
                    className="w-20 text-gray-600 dark:text-gray-400"
                  >
                    Assigned:
                  </Text>
                  <Text>
                    {(() => {
                      const assignedToId =
                        currentLead.assignedTo?._id || currentLead.assignedTo;
                      const member = teamMembers.find(
                        (m) => String(m.id) === String(assignedToId)
                      );
                      return (
                        member?.name ||
                        (currentLead.assignedTo?.firstName &&
                        currentLead.assignedTo?.lastName
                          ? `${currentLead.assignedTo.firstName} ${currentLead.assignedTo.lastName}`
                          : "Unknown User")
                      );
                    })()}
                  </Text>
                </div>
              )}

              <div className="flex items-center">
                <Text strong className="w-20 text-gray-600 dark:text-gray-400">
                  Created:
                </Text>
                <Text>
                  {dayjs(currentLead.createdAt).format("MMM DD, YYYY")}
                </Text>
              </div>
            </div>

            <Divider />

            <div className="space-y-2">
                            {!isCalling ? (
                              <Button
                                type="primary"
                                icon={<PhoneFilled className="icon-flip-horizontal" />}
                                style={{
                                  background: "#52c41a",
                                  borderColor: "#52c41a",
                                  width: "100%"
                                }}
                                onClick={handleStartCall}
                                loading={isInitiatingCall}
                                disabled={isInitiatingCall || !currentLead?.phone}
                              >
                                {isInitiatingCall ? "Initiating Call..." : "Start Call"}
                              </Button>
                            ) : (
                              <Space direction="vertical" style={{ width: '100%' }}>
                                <Button
                                  type="primary"
                                  danger
                                  icon={<PhoneOutlined />}
                                  style={{ width: "100%" }}
                                  onClick={handleEndCall}
                                  loading={false}
                                >
                                  End Call
                                </Button>
                                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                  <Button
                                    icon={<AudioOutlined />}
                                    onClick={handleToggleMute}
                                    style={{ flex: 1 }}
                                  >
                                    Mute
                                  </Button>
                                  <Button
                                    icon={<CustomerServiceOutlined />}
                                    onClick={handleToggleSpeaker}
                                    style={{ flex: 1 }}
                                  >
                                    Speaker
                                  </Button>
                                </Space>
                              </Space>
                            )}
              {/* <Button
                icon={<PhoneOutlined />}
                block
                onClick={() => {
                  manualCallForm.setFieldsValue({ to: currentLead.phone });
                  setManualCallModalVisible(true);
                }}
                className="focus-visible:focus"
                disabled={isConverted || !currentLead?.phone}
              >
                Manual Call
              </Button> */}
              <Button
                icon={<EditOutlined />}
                block
                onClick={() => navigate(`/leads/${currentLead._id}/edit`)}
                className="focus-visible:focus"
                disabled={isConverted}
              >
                Edit Lead
              </Button>
            </div>
          </Card>
        </Col>

        {/* Column 2: Tabs Content (60%) */}
        <Col span={14}>
          <Card className="shadow-soft h-full bg-white dark:bg-gray-800">
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                {
                  key: "requirements",
                  label: "Requirements",
                  children: (
                    <div
                      className="space-y-4"
                      style={{
                        maxHeight: "550px",
                        overflowY: "auto",
                        minHeight: "200px",
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <Title level={5} className="mb-0">
                          Requirements
                        </Title>
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => setRequirementModalVisible(true)}
                          className="focus-visible:focus"
                        >
                          Add Requirement
                        </Button>
                      </div>
                      <List
                        dataSource={currentLead?.requirements || []}
                        locale={{ emptyText: "No requirements added" }}
                        renderItem={(requirement) => (
                          <List.Item
                            actions={[
                              <Button
                                key="edit"
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() =>
                                  handleEditItem("requirement", requirement)
                                }
                              />,
                              <Button
                                key="delete"
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                  handleDeleteItem(
                                    "requirement",
                                    requirement._id
                                  )
                                }
                              />,
                            ]}
                          >
                            <List.Item.Meta
                              title={
                                <div className="flex justify-between items-center">
                                  <Text strong>{requirement.title}</Text>
                                  <Tag
                                    color={
                                      requirement.priority === "urgent"
                                        ? "red"
                                        : requirement.priority === "high"
                                        ? "orange"
                                        : requirement.priority === "medium"
                                        ? "blue"
                                        : "green"
                                    }
                                  >
                                    {requirement.priority?.toUpperCase()}
                                  </Tag>
                                </div>
                              }
                              description={
                                <div>
                                  <Paragraph className="mb-2">
                                    {requirement.description ||
                                      " No description"}
                                  </Paragraph>
                                  {requirement.budget && (
                                    <Text type="secondary">
                                      Budget: {formatCurrency(requirement.budget)}
                                    </Text>
                                  )}
                                </div>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    </div>
                  ),
                },
                {
                  key: "meetings",
                  label: "Meetings",
                  children: (
                    <div
                      className="space-y-4"
                      style={{
                        maxHeight: "550px",
                        overflowY: "auto",
                        minHeight: "200px",
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <Title level={5} className="mb-0">
                          Meetings
                        </Title>
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => {
                            setEditingItem(null);
                            setParentMeeting(null);
                            setMeetingDrawerVisible(true);
                          }}
                          className="focus-visible:focus"
                        >
                          Schedule Meeting
                        </Button>
                      </div>
                      
                      {/* Group meetings by parent-child relationships */}
                      {(() => {
                        // Create a map of parent meetings and their children
                        const meetingsMap = new Map();
                        const parentMeetings = [];
                        
                        // First pass: identify parent meetings and create map entries
                        (currentLead?.meetings || []).forEach(meeting => {
                          if (!meeting.parentMeetingId) {
                            // This is a parent meeting
                            parentMeetings.push(meeting);
                            meetingsMap.set(meeting._id.toString(), {
                              parent: meeting,
                              children: []
                            });
                          }
                        });
                        
                        // Second pass: add child meetings to their parents
                        (currentLead?.meetings || []).forEach(meeting => {
                          if (meeting.parentMeetingId) {
                            const parentId = meeting.parentMeetingId.toString();
                            if (meetingsMap.has(parentId)) {
                              meetingsMap.get(parentId).children.push(meeting);
                            }
                          }
                        });
                        
                        // Sort parent meetings by scheduled date (newest first)
                        parentMeetings.sort((a, b) => 
                          new Date(b.scheduledAt) - new Date(a.scheduledAt)
                        );
                        
                        return (
                          <List
                            dataSource={parentMeetings}
                            locale={{ emptyText: "No meetings scheduled" }}
                            renderItem={(meeting) => {
                              const meetingGroup = meetingsMap.get(meeting._id.toString());
                              const childMeetings = meetingGroup?.children || [];
                              
                              return (
                                <List.Item
                                  className="flex flex-col w-full"
                                  actions={[
                                    <Button
                                      key="edit"
                                      type="text"
                                      size="small"
                                      icon={<EditOutlined />}
                                      onClick={() => {
                                        setEditingItem(meeting);
                                        setParentMeeting(null);
                                        setMeetingDrawerVisible(true);
                                      }}
                                    />,
                                    <Button
                                      key="follow-up"
                                      type="text"
                                      size="small"
                                      onClick={() => {
                                        setEditingItem(null);
                                        setParentMeeting(meeting);
                                        setMeetingDrawerVisible(true);
                                      }}
                                    >
                                      Follow-up
                                    </Button>,
                                    <Button
                                      key="delete"
                                      type="text"
                                      size="small"
                                      danger
                                      icon={<DeleteOutlined />}
                                      onClick={() =>
                                        handleDeleteItem("meeting", meeting._id)
                                      }
                                    />,
                                  ]}
                                >
                                  <div className="w-full">
                                    <List.Item.Meta
                                      avatar={<Avatar icon={<CalendarOutlined />} />}
                                      title={
                                        <div className="flex items-center gap-2">
                                          <span>{meeting.title}</span>
                                          {meeting.status === 'completed' && (
                                            <Tag color="green">Completed</Tag>
                                          )}
                                          {meeting.status === 'cancelled' && (
                                            <Tag color="red">Cancelled</Tag>
                                          )}
                                          {meeting.status === 'rescheduled' && (
                                            <Tag color="orange">Rescheduled</Tag>
                                          )}
                                        </div>
                                      }
                                      description={
                                        <div>
                                          <Text>{meeting.description}</Text>
                                          <br />
                                          <Text type="secondary">
                                            {dayjs(meeting.scheduledAt).format(
                                              "MMM DD, YYYY HH:mm"
                                            )}
                                          </Text>
                                          {meeting.location && (
                                            <>
                                              <br />
                                              <Text type="secondary">
                                                Location: {meeting.location}
                                              </Text>
                                            </>
                                          )}
                                        </div>
                                      }
                                    />
                                    
                                    {/* Render child meetings (follow-ups) */}
                                    {childMeetings.length > 0 && (
                                      <div className="ml-10 mt-2 border-l-2 border-blue-300 pl-4">
                                        <div className="text-xs text-gray-500 mb-1">Follow-up Meetings:</div>
                                        {childMeetings.map((childMeeting) => (
                                          <div key={childMeeting._id} className="mb-3">
                                            <div className="flex justify-between">
                                              <div>
                                                <Badge color="blue" />
                                                <Text strong className="ml-1">{childMeeting.title}</Text>
                                              </div>
                                              <Space>
                                                <Button
                                                  type="text"
                                                  size="small"
                                                  icon={<EditOutlined />}
                                                  onClick={() => {
                                                    setEditingItem(childMeeting);
                                                    setParentMeeting(null);
                                                    setMeetingDrawerVisible(true);
                                                  }}
                                                />
                                                <Button
                                                  type="text"
                                                  size="small"
                                                  danger
                                                  icon={<DeleteOutlined />}
                                                  onClick={() =>
                                                    handleDeleteItem("meeting", childMeeting._id)
                                                  }
                                                />
                                              </Space>
                                            </div>
                                            <div className="ml-4">
                                              <Text type="secondary" className="text-xs">
                                                {dayjs(childMeeting.scheduledAt).format("MMM DD, YYYY HH:mm")}
                                              </Text>
                                              {childMeeting.location && (
                                                <>
                                                  <br />
                                                  <Text type="secondary" className="text-xs">
                                                    Location: {childMeeting.location}
                                                  </Text>
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </List.Item>
                              );
                            }}
                          />
                        );
                      })()}
                    </div>
                  ),
                },
                {
                  key: "calllogs",
                  label: (
                    <span>
                      <PhoneFilled /> Call Logs
                    </span>
                  ),
                  children: (
                    <div style={{ maxHeight: "550px", overflowY: "auto", minHeight: "200px" }}>
                      <div className="mb-4">
                        <Title level={5}>Call History</Title>
                      </div>
                      {isLoadingCallLogs ? (
                        <div className="flex justify-center items-center h-32">
                          <Spin />
                        </div>
                      ) : callLogs.length > 0 ? (
                        <div className="space-y-4">
                          {callLogs.map((log) => {
                            // Hide SIP addresses and provider internals from list
                            const showFrom = log.from && !/^sip:/i.test(log.from) && !/phone\.plivo/i.test(log.from);
                            // Hide raw numeric 'to' values (like 916200502473)
                            const showTo = log.to && !/^\+?\d+$/.test(String(log.to));
                            // Hide generic hangup reason 'NORMAL_CLEARING'
                            const showHangup = log.hangupCause && String(log.hangupCause).toUpperCase() !== 'NORMAL_CLEARING';

                            return (
                              <Card 
                                key={log.callUUID} 
                                size="small"
                                className="shadow-sm"
                                styles={{ body: { padding: '16px' } }}
                              >
                                <div className="flex items-start gap-4">
                                  {/* Left Column: Icon */}
                                  <div className="flex-shrink-0">
                                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50">
                                      {log.direction === 'inbound' ? (
                                        <PhoneFilled style={{ color: '#1890ff', fontSize: '18px' }} />
                                      ) : (
                                        <PhoneFilled style={{ color: '#52c41a', fontSize: '18px' }} />
                                      )}
                                    </div>
                                  </div>

                                  {/* Middle Column: Call Details */}
                                  <div className="flex-grow min-w-0">
                                    {/* Header Row */}
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <Text strong>
                                          {log.direction === 'inbound' ? 'Outgoinng' : 'Incoming'} Call
                                        </Text>
                                        {log.status && (
                                          <Tag color={log.status === 'completed' ? 'success' : 'warning'}>
                                            {log.status}
                                          </Tag>
                                        )}
                                      </div>
                                      <Text type="secondary" className="text-sm">
                                        {dayjs(log.startTime).format('MMM D, YYYY h:mm A')}
                                      </Text>
                                    </div>

                                    {/* Duration and Cost */}
                                    <div className="flex gap-4 mb-2 text-gray-600">
                                      <span>Duration: {log.duration}s</span>
                                      {log.billDuration && (
                                        <span>Cost: ${log.totalCost}</span>
                                      )}
                                    </div>

                                    {/* From/To Details */}
                                    {showFrom && (
                                      <div className="text-sm text-gray-700">
                                        From: <Text strong>{log.from}</Text>
                                      </div>
                                    )}
                                    {showTo && (
                                      <div className="text-sm text-gray-600 mt-1">
                                        To: <Text type="secondary">{log.to}</Text>
                                      </div>
                                    )}
                                    {showHangup && (
                                      <div className="text-sm text-gray-500 mt-1">
                                        Hangup: <Text type="secondary">{log.hangupCause}</Text>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </Card>
                            );
                          })}
                        </div>
                      ) : (
                        <Empty description="No call logs found" />
                      )}
                    </div>
                  ),
                },
                {
                  key: "notes",
                  label: "Notes",
                  children: (
                    <div
                      className="space-y-4"
                      style={{
                        maxHeight: "550px",
                        overflowY: "auto",
                        minHeight: "200px",
                      }}
                    >
                      <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800/40">
                        <div className="mb-3">
                          <Title level={5} className="mb-1">
                            Presales Notes
                          </Title>
                          <Text type="secondary" className="text-xs">
                            Capture stakeholder map, BANT details, and any presales context.
                          </Text>
                        </div>
                        <TextArea
                          rows={5}
                          maxLength={1500}
                          showCount
                          value={presalesNotesDraft}
                          onChange={(e) => setPresalesNotesDraft(e.target.value)}
                          placeholder="Capture presales context, requirements, BANT notes, stakeholder map..."
                          style={{ resize: "vertical" }}
                        />
                        <div className="flex justify-end mt-4">
                          <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={savingPresalesNotes}
                            disabled={presalesNotesDraft === (currentLead?.presalesNotes || "")}
                            onClick={handleSavePresalesNotes}
                          >
                            Save Presales Notes
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <Title level={5} className="mb-0">
                          Notes
                        </Title>
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => {
                            setEditingItem(null);
                            noteForm.resetFields();
                            setNoteModalVisible(true);
                          }}
                          className="focus-visible:focus"
                        >
                          Add Note
                        </Button>
                      </div>
                      <List
                        dataSource={currentLead?.notes || []}
                        locale={{ emptyText: "No notes added" }}
                        renderItem={(note) => (
                          <List.Item
                            actions={[
                              <Button
                                key="edit"
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => handleEditItem("note", note)}
                              />,
                              <Button
                                key="delete"
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                  handleDeleteItem("note", note._id)
                                }
                              />,
                            ]}
                          >
                            <List.Item.Meta
                              avatar={<Avatar icon={<FileTextOutlined />} />}
                              title={
                                <div className="flex justify-between">
                                  <Text strong>Note</Text>
                                  <Text type="secondary" className="text-sm">
                                    {dayjs(note.createdAt).format(
                                      "MMM DD, YYYY HH:mm"
                                    )}
                                  </Text>
                                </div>
                              }
                              description={
                                <Paragraph>{note.content}</Paragraph>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    </div>
                  ),
                },
                {
                  key: "documents",
                  label: "Documents",
                  children: (
                    <div
                      className="space-y-4"
                      style={{
                        maxHeight: "550px",
                        overflowY: "auto",
                        minHeight: "200px",
                      }}
                    >
                      <div className="flex justify-between items-center mb-4">
                        <Title level={5} className="mb-0">
                          Documents
                        </Title>
                        <Space>
                          <Button
                            type="default"
                            icon={<FilePdfOutlined />}
                            onClick={() => setProposalDrawerVisible(true)}
                            size="small"
                          >
                            Create Proposal
                          </Button>
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setDocumentModalVisible(true)}
                            size="small"
                          >
                            Upload Document
                          </Button>
                        </Space>
                      </div>
                      <Upload.Dragger
                        name="document"
                        multiple
                        customRequest={async ({ file, onSuccess, onError, onProgress }) => {
                          const fileId = `${file.uid}-${Date.now()}`;

                          // Add file to uploading list
                          setUploadingFiles(prev => [...prev, {
                            id: fileId,
                            name: file.name,
                            percent: 0
                          }]);

                          try {
                            const formData = new FormData();
                            formData.append('document', file);

                            const response = await apiService.post(
                              `/leads/${id}/documents/upload`,
                              formData,
                              {
                                headers: {
                                  'Content-Type': 'multipart/form-data',
                                },
                                onUploadProgress: (progressEvent) => {
                                  const percent = Math.round(
                                    (progressEvent.loaded * 100) / progressEvent.total
                                  );
                                  onProgress({ percent });

                                  // Update progress in state
                                  setUploadingFiles(prev =>
                                    prev.map(f =>
                                      f.id === fileId ? { ...f, percent } : f
                                    )
                                  );
                                },
                              }
                            );

                            onSuccess(response.data);
                            message.success(`${file.name} uploaded successfully`);

                            // Remove from uploading list
                            setUploadingFiles(prev => prev.filter(f => f.id !== fileId));

                            // Refresh lead data to show new document
                            await fetchLeadById(id);
                          } catch (error) {
                            console.error('Upload error:', error);
                            onError(error);

                            // Remove from uploading list
                            setUploadingFiles(prev => prev.filter(f => f.id !== fileId));

                            message.error(
                              error.response?.data?.message ||
                              `${file.name} upload failed`
                            );
                          }
                        }}
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                        showUploadList={false}
                        disabled={uploadingFiles.length > 0}
                      >
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">
                          Click or drag file to this area to upload
                        </p>
                        <p className="ant-upload-hint">
                          Supports: Images, PDF, Word, Excel, PowerPoint, Text, CSV (Max 20MB)
                        </p>
                      </Upload.Dragger>

                      {uploadingFiles.length > 0 && (
                        <div style={{ marginTop: '16px' }}>
                          {uploadingFiles.map(file => (
                            <div key={file.id} style={{ marginBottom: '8px' }}>
                              <Text>{file.name}</Text>
                              <Progress percent={file.percent} status="active" />
                            </div>
                          ))}
                        </div>
                      )}

                      <List
                        dataSource={currentLead.documents || []}
                        locale={{ emptyText: "No documents uploaded" }}
                        renderItem={(doc) => (
                          <List.Item
                            actions={[
                              <Button
                                type="link"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownloadDocument(doc)}
                              >
                                Download
                              </Button>,
                            ]}
                          >
                            <List.Item.Meta
                              avatar={<Avatar icon={<FileOutlined />} />}
                              title={doc.name}
                              description={`${doc.size ? `${Math.round(doc.size / 1024)} KB` : 'Unknown size'} • ${doc.uploadedAt ? dayjs(doc.uploadedAt).format("MMM DD, YYYY") : dayjs(doc.createdAt).format("MMM DD, YYYY")}`}
                            />
                          </List.Item>
                        )}
                      />
                    </div>
                  ),
                },
                {
                  key: "tasks",
                  label: "Tasks",
                  children: (
                    <div style={{ padding: "20px" }}>
                      <div
                        style={{
                          marginBottom: "16px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <h4>Tasks ({currentLead?.tasks?.length || 0})</h4>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          size="small"
                          onClick={handleAddTask}
                        >
                          Add Task
                        </Button>
                      </div>

                      {!currentLead?.tasks || currentLead.tasks.length === 0 ? (
                        <Empty
                          description="No tasks found"
                          style={{ margin: "40px 0" }}
                        />
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            maxHeight: "340px" /* Adjusted for padding */,
                            overflowY: "auto",
                            minHeight: "140px" /* Adjusted for padding */,
                          }}
                        >
                          {currentLead.tasks.map((task, index) => (
                            <Card
                              key={task._id || index}
                              size="small"
                              style={{ border: "1px solid #f0f0f0" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      fontWeight: "bold",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    {task.title}
                                  </div>
                                  {task.description && (
                                    <div
                                      style={{
                                        color: "#666",
                                        marginBottom: "8px",
                                        fontSize: "14px",
                                      }}
                                    >
                                      {task.description}
                                    </div>
                                  )}
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "8px",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <Tag
                                      color={
                                        task.priority === "high"
                                          ? "red"
                                          : task.priority === "medium"
                                          ? "orange"
                                          : "blue"
                                      }
                                    >
                                      {task.priority?.toUpperCase() || "MEDIUM"}
                                    </Tag>
                                    <Tag
                                      color={
                                        task.status === "completed"
                                          ? "green"
                                          : task.status === "in_progress"
                                          ? "blue"
                                          : "orange"
                                      }
                                    >
                                      {task.status
                                        ?.replace("_", " ")
                                        .toUpperCase() || "PENDING"}
                                    </Tag>
                                    {task.dueDate && (
                                      <Tag color="purple">
                                        Due:{" "}
                                        {new Date(
                                          task.dueDate
                                        ).toLocaleDateString()}
                                      </Tag>
                                    )}
                                  </div>
                                </div>
                                <div style={{ display: "flex", gap: "8px" }}>
                                  <Button
                                    size="small"
                                    type="link"
                                    onClick={() => handleEditTask(task)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    type="link"
                                    danger
                                    onClick={() => handleDeleteTask(task._id)}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: "email",
                  label: (
                    <span>
                      <MailOutlined /> Email
                    </span>
                  ),
                  children: (
                    <Tabs
                      defaultActiveKey="compose"
                      items={[
                        {
                          key: "compose",
                          label: "Compose & Send",
                          children: (
                            <div style={{ maxHeight: "550px", overflowY: "auto", padding: "16px" }}>
                              <EmailCompose
                                leadId={currentLead?._id}
                                leadEmail={currentLead?.email}
                                leadName={`${currentLead?.firstName || ''} ${currentLead?.lastName || ''}`.trim()}
                                onEmailSent={() => {
                                  setEmailRefreshTrigger(prev => prev + 1);
                                  fetchLeadById(id); // Refresh lead data to update activity timeline
                                  messageApi.success('Email sent! Check the History tab to view.');
                                }}
                              />
                            </div>
                          )
                        },
                        {
                          key: "history",
                          label: "Email History",
                          children: (
                            <div style={{ padding: "16px" }}>
                              <EmailHistory
                                leadId={currentLead?._id}
                                refreshTrigger={emailRefreshTrigger}
                              />
                            </div>
                          )
                        }
                      ]}
                    />
                  ),
                },
                ...(isWhatsAppEnabled ? [{
                  key: "whatsapp",
                  label: (
                    <span>
                      <WhatsAppOutlined /> WhatsApp
                    </span>
                  ),
                  children: (
                    <div
                      style={{
                        height: "550px",
                        display: "flex",
                        flexDirection: "column",
                        background: "#f0f2f5",
                      }}
                    >
                      {/* Chat Header */}
                      <div
                        style={{
                          padding: "16px",
                          background: "#25D366",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <Avatar
                          size={40}
                          style={{ background: "#128C7E" }}
                          icon={<UserOutlined />}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                            {currentLead.firstName} {currentLead.lastName}
                          </div>
                          <div style={{ fontSize: "12px", opacity: 0.9 }}>
                            {currentLead.phone || "No phone number"}
                          </div>
                        </div>
                        <Tag color="success">Active</Tag>
                      </div>

                      {whatsappUnavailable && (
                        <div style={{ padding: "12px 16px" }}>
                          <Alert
                            type="warning"
                            showIcon
                            message="WhatsApp integration is unavailable"
                            description="Please verify the WhatsApp configuration or try again later."
                          />
                        </div>
                      )}

                      {/* Chat Messages Area */}
                      <div
                        ref={chatContainerRef}
                        style={{
                          flex: 1,
                          overflowY: "auto",
                          padding: "16px",
                          backgroundImage:
                            "url('data:image/svg+xml,%3Csvg width=\"100\" height=\"100\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cpath d=\"M0 0h100v100H0z\" fill=\"%23e5ddd5\"/%3E%3Cpath d=\"M25 25l5 5-5 5-5-5z\" fill=\"%23d1d7db\" opacity=\".1\"/%3E%3C/svg%3E')",
                        }}
                      >
                        {isChatLoading ? (
                          <div className="flex justify-center items-center h-full">
                            <Spin size="large" />
                          </div>
                        ) : chatMessages.length === 0 ? (
                          <Empty
                            description="No messages yet"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            style={{ marginTop: "100px" }}
                          />
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            {chatMessages.map((msg) => (
                              <div
                                key={msg._id}
                                style={{
                                  display: "flex",
                                  justifyContent:
                                    msg.entity === "user" ? "flex-start" : "flex-end",
                                }}
                              >
                                <div
                                  style={{
                                    maxWidth: "70%",
                                    background: msg.entity === "user" ? "white" : "#dcf8c6",
                                    padding: "8px 12px",
                                    borderRadius: "8px",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  <div style={{ fontSize: "14px", marginBottom: "4px" }}>
                                    {msg.message}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#667781",
                                      textAlign: "right",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-end",
                                      gap: "4px",
                                    }}
                                  >
                                    {dayjs(msg.timestamp).format("HH:mm A")}
                                    {msg.entity === "bot" && (
                                      <CheckCircleOutlined style={{ fontSize: "12px", color: "#53bdeb" }} />
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Message Input Area */}
                      <div
                        style={{
                          padding: "12px 16px",
                          background: "#f0f2f5",
                          borderTop: "1px solid #e0e0e0",
                        }}
                      >
                        <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                          <Input.TextArea
                            placeholder="Type a message..."
                            autoSize={{ minRows: 1, maxRows: 4 }}
                            style={{
                              flex: 1,
                              borderRadius: "20px",
                              padding: "8px 16px",
                            }}
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            onPressEnter={(e) => {
                              if (!e.shiftKey) {
                                e.preventDefault();
                                handleSendChatMessage();
                              }
                            }}
                            disabled={whatsappUnavailable}
                          />
                          <Button
                            type="primary"
                            shape="circle"
                            icon={<SendOutlined />}
                            size="large"
                            style={{
                              background: "#25D366",
                              borderColor: "#25D366",
                            }}
                            onClick={handleSendChatMessage}
                            disabled={whatsappUnavailable}
                          />
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#667781",
                            marginTop: "8px",
                            textAlign: "center",
                          }}
                        >
                          {whatsappUnavailable
                            ? "WhatsApp messaging is currently unavailable"
                            : "Press Enter to send • Shift+Enter for new line"}
                        </div>
                      </div>
                    </div>
                  ),
                }] : []),
                // Conditionally add AI Calling tab based on environment variable
                ...(isAICallingEnabled ? [{
                  key: "aicalling",
                  label: (
                    <span>
                      <CustomerServiceOutlined /> AI Calling
                    </span>
                  ),
                  children: (
                    <div
                      style={{
                        height: "550px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Call Header */}
                      <div
                        style={{
                          padding: "16px 20px",
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                          <Avatar
                            size={48}
                            style={{ 
                              background: "rgba(255, 255, 255, 0.2)",
                              border: "2px solid white"
                            }}
                            icon={<CustomerServiceOutlined style={{ fontSize: "24px" }} />}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "2px" }}>
                              {currentLead.firstName} {currentLead.lastName}
                            </div>
                            <div style={{ fontSize: "12px", opacity: 0.9 }}>
                              {currentLead.phone || "No phone number"}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <Button
                              type="primary"
                              size="small"
                              icon={<PhoneFilled className="icon-flip-horizontal" />}
                              style={{
                                background: "#52c41a",
                                borderColor: "#52c41a",
                              }}
                              onClick={handleStartAICallCampaign}
                              loading={isCalling}
                              disabled={isCalling}
                            >
                              {isCalling ? "Starting Call..." : "Start Call"}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Single Column Layout for Call Transcript */}
                          <div
                            style={{
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              background: "#fafafa",
                            }}
                          >
                            <div
                              style={{
                                padding: "12px 16px",
                                background: "white",
                                borderBottom: "1px solid #f0f0f0",
                                fontWeight: "bold",
                                fontSize: "14px",
                              }}
                            >
                              <FileTextOutlined style={{ marginRight: "8px" }} />
                              Call Transcript
                            </div>
                            <div
                              style={{
                                flex: 1,
                                overflowY: "auto",
                                padding: "16px",
                              }}
                            >
                              {isTranscriptsLoading ? (
                                <div className="flex justify-center items-center h-full">
                                  <Spin size="large" />
                                </div>
                              ) : transcripts.length === 0 ? (
                                <Empty
                                  description="No call transcripts available for this lead."
                                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                                  style={{ marginTop: "100px" }}
                                />
                              ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                                  {transcripts.map((call, callIndex) => (
                                    <div key={call.id || callIndex} style={{ border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px", background: "white" }}>
                                      <div style={{ marginBottom: "12px", paddingBottom: "8px", borderBottom: "1px solid #f0f0f0" }}>
                                        <Title level={5} style={{ margin: 0 }}>Call ID: {call.id}</Title>
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                          Status: <Tag color={call.status === "completed" ? "green" : "orange"}>{call.status?.toUpperCase()}</Tag>
                                          Duration: {call.duration} seconds
                                          <br />
                                          Start: {dayjs(call.callStartDate).format("MMM DD, YYYY HH:mm:ss")}
                                          <br />
                                          End: {dayjs(call.callEndDate).format("MMM DD, YYYY HH:mm:ss")}
                                        </Text>
                                      </div>
                                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                        {call.transcript.map((entry, entryIndex) => (
                                          <div
                                            key={entryIndex}
                                            style={{
                                              display: "flex",
                                              justifyContent: entry.role === "assistant" ? "flex-end" : "flex-start",
                                            }}
                                          >
                                            <div
                                              style={{
                                                maxWidth: "70%", // Limit width to prevent messages from spanning full width
                                                background: entry.role === "assistant" ? "#e6f7ff" : "#f5f5f5",
                                                padding: "10px 12px",
                                                borderRadius: "8px",
                                                borderLeft: entry.role === "assistant" ? "3px solid #1890ff" : "3px solid #d9d9d9",
                                                alignSelf: entry.role === "assistant" ? "flex-end" : "flex-start",
                                              }}
                                            >
                                              <div style={{ fontSize: "11px", color: "#999", marginBottom: "4px", textAlign: entry.role === "assistant" ? "right" : "left" }}>
                                                {entry.role === "assistant" ? "AI Assistant" : currentLead.firstName}
                                              </div>
                                              <div>
                                                {entry.content}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                    </div>
                  ),
                }] : []),
              ]}
            />
          </Card>
        </Col>

        {/* Column 3: Timeline (18%) */}
        <Col span={5}>
          <Card
            className="shadow-soft h-full bg-white dark:bg-gray-800"
            title="Activity Timeline"
          >
            <Timeline
              items={[
                {
                  dot: <CheckCircleOutlined className="text-green-500" />,
                  children: (
                    <div>
                      <Text strong>Lead Created</Text>
                      <br />
                      <Text type="secondary" className="text-sm">
                        {dayjs(currentLead.createdAt).format(
                          "MMM DD, YYYY HH:mm"
                        )}
                      </Text>
                      <br />
                      <Text className="text-sm">
                        Lead was added to the system
                      </Text>
                    </div>
                  ),
                },
                ...(currentLead.activities || []).map((activity, index) => ({
                  dot:
                    activity.type === "call" ? (
                      <PhoneOutlined className="text-blue-500 icon-flip-horizontal" />
                    ) : activity.type === "email" ? (
                      <MailOutlined className="text-purple-500" />
                    ) : activity.type === "meeting" ? (
                      <CalendarOutlined className="text-orange-500" />
                    ) : (
                      <ClockCircleOutlined className="text-gray-500" />
                    ),
                  children: (
                    <div key={`activity-${index}`}>
                      <Text strong>{activity.subject}</Text>
                      <br />
                      <Text type="secondary" className="text-sm">
                        {dayjs(activity.createdAt).format("MMM DD, YYYY HH:mm")}
                      </Text>
                      <br />
                      <Text className="text-sm">{activity.description}</Text>
                    </div>
                  ),
                })),
                ...(currentLead.tasks || []).map((task, index) => ({
                  dot: <CheckSquareOutlined className="text-indigo-500" />,
                  children: (
                    <div key={`task-${index}`}>
                      <Text strong>Task: {task.title}</Text>
                      <br />
                      <Text type="secondary" className="text-sm">
                        {dayjs(task.createdAt || task.dueDate).format("MMM DD, YYYY HH:mm")}
                      </Text>
                      <br />
                      <Text className="text-sm">
                        {task.description || `Priority: ${task.priority}, Status: ${task.status}`}
                      </Text>
                    </div>
                  ),
                })),
                {
                  dot: (
                    <ExclamationCircleOutlined className="text-orange-500" />
                  ),
                  children: (
                    <div>
                      <Text strong>Follow-up Required</Text>
                      <br />
                      <Text type="secondary" className="text-sm">
                        {currentLead.expectedCloseDate
                          ? dayjs(currentLead.expectedCloseDate).format(
                              "MMM DD, YYYY"
                            )
                          : "No date set"}
                      </Text>
                      <br />
                      <Text className="text-sm">Next action needed</Text>
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Add Note Modal */}
      <Modal
        title={editingItem?.type === "note" ? "Edit Note" : "Add Note"}
        open={noteModalVisible}
        onCancel={() => {
          setNoteModalVisible(false);
          setEditingItem(null);
          noteForm.resetFields();
        }}
        footer={null}
      >
        <Form form={noteForm} onFinish={handleUpdateNote} layout="vertical">
          <Form.Item
            name="content"
            label="Note Content"
            rules={[
              { required: true, message: "Please enter notes" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(
                      new Error("No leading or trailing spaces allowed")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea rows={4} placeholder="Enter your note here..." />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setNoteModalVisible(false);
                  setEditingItem(null);
                  noteForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingItem?.type === "note" ? "Update Note" : "Add Note"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Activity Modal */}
      <Modal
        title="Add Activity"
        open={activityModalVisible}
        onCancel={() => setActivityModalVisible(false)}
        footer={null}
      >
        <Form
          form={activityForm}
          onFinish={handleAddActivity}
          layout="vertical"
        >
          <Form.Item
            name="type"
            label="Activity Type"
            rules={[{ required: true, message: "Please select activity type" }]}
          >
            <Select onChange={setActivityType}>
              <Option value="call">Call</Option>
              <Option value="email">Email</Option>
              <Option value="meeting">Meeting</Option>
              <Option value="task">Task</Option>
              <Option value="note">Note</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: "Please enter subject" }]}
          >
            <Input placeholder="Activity subject" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Activity description" />
          </Form.Item>
          <Form.Item name="scheduledAt" label="Scheduled Date">
            <DatePicker showTime className="w-full" />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button onClick={() => setActivityModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Add Activity
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Requirement Modal */}
      {/* <Modal
        title={
          editingItem?.type === "requirement"
            ? "Edit Requirement"
            : "Add Requirement"
        }
        open={requirementModalVisible}
        onCancel={() => {
          setRequirementModalVisible(false);
          setEditingItem(null);
          requirementForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={requirementForm}
          onFinish={handleAddRequirement}
          layout="vertical"
        >
          <Form.Item
            name="title"
            label="Requirement Title"
            rules={[
              { required: true, message: "Please enter requirement title" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(new Error("No leading or trailing spaces allowed"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Enter requirement title" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
           rules={[
              { required: true, message: "Please enter requirement title" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(new Error("No leading or trailing spaces allowed"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Describe the requirement in detail"
            />
          </Form.Item>
          <Form.Item
            name="priority"
            label="Priority"
            rules={[{ required: true, message: "Please select priority" }]}
          >
            <Select placeholder="Select priority">
              <Option value="low">Low</Option>
              <Option value="medium">Medium</Option>
              <Option value="high">High</Option>
              <Option value="urgent">Urgent</Option>
            </Select>
          </Form.Item>
          <Form.Item name="budget" label="Budget Range">
            <Input placeholder={`e.g., ${currencySymbol}10,000 - ${currencySymbol}50,000`} />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setRequirementModalVisible(false);
                  setEditingItem(null);
                  requirementForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingItem?.type === "requirement" ? "Update" : "Add"}{" "}
                Requirement
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal> */}

      {/* Meeting Modal */}
      {/* <Modal
        title={
          editingItem?.type === "meeting" ? "Edit Meeting" : "Schedule Meeting"
        }
        open={meetingModalVisible}
        onCancel={() => {
          setMeetingModalVisible(false);
          setEditingItem(null);
          meetingForm.resetFields();
        }}
        footer={null}
      >
        <Form form={meetingForm} onFinish={handleAddMeeting} layout="vertical">
          <Form.Item
            name="title"
            label="Meeting Title"
            rules={[{ required: true, message: "Please enter meeting title" }]}
          >
            <Input placeholder="Enter meeting title" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Meeting agenda or description" />
          </Form.Item>
          <Form.Item
            name="scheduledAt"
            label="Date & Time"
            rules={[{ required: true, message: "Please select date and time" }]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              className="w-full"
              placeholder="Select date and time"
            />
          </Form.Item>
          <Form.Item name="duration" label="Duration (minutes)">
            <InputNumber
              min={15}
              max={480}
              placeholder="60"
              className="w-full"
            />
          </Form.Item>
          <Form.Item name="location" label="Location">
            <Input placeholder="Meeting location or video link" />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setMeetingModalVisible(false);
                  setEditingItem(null);
                  meetingForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingItem?.type === "meeting" ? "Update" : "Schedule"}{" "}
                Meeting
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal> */}

      {/* Call Modal */}
      {/* <Modal
        title={editingItem?.type === "call" ? "Edit Call" : "Log Call"}
        open={callModalVisible}
        onCancel={() => {
          setCallModalVisible(false);
          setEditingItem(null);
          callForm.resetFields();
        }}
        footer={null}
      >
        <Form form={callForm} onFinish={handleAddCall} layout="vertical">
          <Form.Item
            name="type"
            label="Call Type"
            rules={[{ required: true, message: "Please select call type" }]}
          >
            <Select placeholder="Select call type">
              <Option value="inbound">Inbound</Option>
              <Option value="outbound">Outbound</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="date"
            label="Call Date & Time"
            rules={[
              { required: true, message: "Please select call date and time" },
            ]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              className="w-full"
              placeholder="Select call date and time"
            />
          </Form.Item>
          <Form.Item name="duration" label="Duration (minutes)">
            <InputNumber
              min={1}
              max={300}
              placeholder="15"
              className="w-full"
            />
          </Form.Item>
          <Form.Item name="outcome" label="Call Outcome">
            <Select placeholder="Select outcome">
              <Option value="successful">Successful</Option>
              <Option value="voicemail">Voicemail</Option>
              <Option value="no_answer">No Answer</Option>
              <Option value="busy">Busy</Option>
              <Option value="follow_up_required">Follow-up Scheduled</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="notes"
            label="Call Notes"
            rules={[{ required: true, message: "Please enter call notes" }]}
          >
            <TextArea
              rows={4}
              placeholder="What was discussed during the call?"
            />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setCallModalVisible(false);
                  setEditingItem(null);
                  callForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingItem?.type === "call" ? "Update" : "Log"} Call
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal> */}

      {/* Document Upload Modal */}
      <Modal
        title="Upload Document"
        open={documentModalVisible}
        onCancel={() => setDocumentModalVisible(false)}
        footer={null}
      >
        <div className="space-y-4">
          <Upload.Dragger
            name="files"
            multiple
            action="/api/upload"
            onChange={(info) => {
              if (info.file.status === "done") {
                message.success(`${info.file.name} file uploaded successfully`);
                setDocumentModalVisible(false);
              } else if (info.file.status === "error") {
                message.error(`${info.file.name} file upload failed.`);
              }
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for single or bulk upload. Documents will be attached to
              this lead.
            </p>
          </Upload.Dragger>
        </div>
      </Modal>

      {/* Requirement Modal */}
      <Modal
        title={editingItem ? "Edit Requirement" : "Add Requirement"}
        open={requirementModalVisible}
        onCancel={() => {
          setRequirementModalVisible(false);
          requirementForm.resetFields();
          setEditingItem(null);
        }}
        footer={null}
      >
        <Form
          form={requirementForm}
          onFinish={
            editingItem ? handleUpdateRequirement : handleAddRequirement
          }
          layout="vertical"
        >
          <Form.Item
            name="title"
            label="Requirement title"
            rules={[
              { required: true, message: "Please enter requirement title" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(
                      new Error("No leading or trailing spaces allowed")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Enter requirement title" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter requirement description" />
          </Form.Item>
          <Form.Item name="priority" label="Priority" initialValue="medium">
            <Select>
              <Option value="low">Low</Option>
              <Option value="medium">Medium</Option>
              <Option value="high">High</Option>
              <Option value="critical">Critical</Option>
            </Select>
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="pending">
            <Select>
              <Option value="pending">Pending</Option>
              <Option value="in_progress">In Progress</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Form.Item>
          <Form.Item name="budget" label={`Budget (${currencySymbol})`}>
            <InputNumber
              min={0}
              className="w-full"
              prefix={currencySymbol}
              placeholder="Enter budget"
            />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setRequirementModalVisible(false);
                  requirementForm.resetFields();
                  setEditingItem(null);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingItem ? "Update" : "Add"} Requirement
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Meeting Drawer */}
      <MeetingDrawer
        visible={meetingDrawerVisible}
        onClose={() => {
          setMeetingDrawerVisible(false);
          setEditingItem(null);
          setParentMeeting(null);
        }}
        onFinish={(meetingData, followUpData) => {
          if (editingItem) {
            handleUpdateMeeting(meetingData);
          } else {
            handleAddMeeting(meetingData, followUpData);
          }
        }}
        editingMeeting={editingItem}
        parentMeeting={parentMeeting}
      />

      {/* Call Modal */}
      <Modal
        title={editingItem ? "Edit Call" : "Log Call"}
        open={callModalVisible}
        onCancel={() => {
          setCallModalVisible(false);
          callForm.resetFields();
          setEditingItem(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={callForm}
          onFinish={editingItem ? handleUpdateCall : handleAddCall}
          layout="vertical"
          initialValues={{
            type: "outbound",
            outcome: "successful",
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Call Type"
                rules={[{ required: true, message: "Please select call type" }]}
              >
                <Select placeholder="Select call type">
                  <Option value="inbound">📞 Inbound</Option>
                  <Option value="outbound">📱 Outbound</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="outcome"
                label="Call Outcome"
                rules={[
                  { required: true, message: "Please select call outcome" },
                ]}
              >
                <Select placeholder="Select outcome">
                  <Option value="successful">✅ Successful</Option>
                  <Option value="no_answer">❌ No Answer</Option>
                  <Option value="busy">📵 Busy</Option>
                  <Option value="voicemail">📧 Voicemail</Option>
                  <Option value="follow_up_required">
                    🔄 Follow-up Required
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="subject"
            label="Call Subject"
            rules={[
              { required: true, message: "Please enter call subject" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(
                      new Error("No leading or trailing spaces allowed")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Enter call subject (e.g., Follow-up on proposal)" />
          </Form.Item>
          <Form.Item
            name="notes"
            label="Call Notes"
            rules={[
              { required: true, message: "Please enter call notes" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(
                      new Error("No leading or trailing spaces allowed")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea rows={4} placeholder="Enter call notes and details" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="duration" label="Duration (minutes)">
                <InputNumber
                  min={0}
                  className="w-full"
                  placeholder="Enter duration"
                  addonAfter="min"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="scheduledAt" label="Call Date & Time">
                <DatePicker
                  showTime
                  className="w-full"
                  placeholder="Select date & time"
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setCallModalVisible(false);
                  callForm.resetFields();
                  setEditingItem(null);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingItem ? "Update" : "Log"} Call
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Document Modal */}
      <Modal
        title="Add Document"
        open={documentModalVisible}
        onCancel={() => setDocumentModalVisible(false)}
        footer={null}
      >
        <Form onFinish={handleAddDocument} layout="vertical">
          <Form.Item
            name="name"
            label="Document Name"
            rules={[{ required: true, message: "Please enter document name" }]}
          >
            <Input placeholder="Enter document name" />
          </Form.Item>
          <Form.Item name="type" label="Document Type" initialValue="other">
            <Select>
              <Option value="contract">Contract</Option>
              <Option value="proposal">Proposal</Option>
              <Option value="invoice">Invoice</Option>
              <Option value="presentation">Presentation</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="url"
            label="Document URL"
            rules={[{ required: true, message: "Please enter document URL" }]}
          >
            <Input placeholder="Enter document URL or file path" />
          </Form.Item>
          <Form.Item name="size" label="File Size (bytes)">
            <InputNumber
              min={0}
              className="w-full"
              placeholder="Enter file size"
            />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter document description" />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button onClick={() => setDocumentModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Add Document
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Convert to Deal Modal */}
      <Modal
        title="Convert Lead to Deal"
        open={convertToDealModalVisible}
        onCancel={() => {
          if (!isLoading) {
            setConvertToDealModalVisible(false);
            convertForm.resetFields();
          }
        }}
        footer={null}
        maskClosable={!isLoading}
      >
        {isLoading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1000,
            borderRadius: '8px'
          }}>
            <Spin size="large" tip="Converting lead to deal..." />
          </div>
        )}
        <Form
          form={convertForm}
          onFinish={handleConvertToDeal}
          layout="vertical"
          style={{ opacity: isLoading ? 0.5 : 1, pointerEvents: isLoading ? 'none' : 'auto' }}
          initialValues={{
            title: `${currentLead?.firstName} ${currentLead?.lastName} - ${
              currentLead?.company || "Deal"
            }`,
            value: currentLead?.estimatedValue,
            expectedCloseDate: currentLead?.expectedCloseDate
              ? dayjs(currentLead.expectedCloseDate)
              : null,
            stage: "opportunity_qualified",
          }}
        >
          <Form.Item
            name="title"
            label="Deal Title"
            rules={[{ required: true, message: "Please enter deal title" }]}
          >
            <Input placeholder="Enter deal title" />
          </Form.Item>
          <Form.Item
            name="value"
            label={`Deal Value (${currencySymbol})`}
            rules={[{ required: true, message: "Please enter deal value" }]}
          >
            <InputNumber
              min={0}
              formatter={(value) =>
                `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/₹\s?|(,*)/g, "")}
              className="w-full"
              placeholder="Enter deal value"
            />
          </Form.Item>
          <Form.Item
            name="stage"
            label="Deal Stage"
            rules={[{ required: true, message: "Please select deal stage" }]}
          >
            <Select placeholder="Select deal stage">
              <Option value="opportunity_qualified">Opportunity Qualified</Option>
              <Option value="proposal_in_progress">Proposal in Progress</Option>
              <Option value="poc">Proof of Concept (POC)</Option>
              <Option value="proposal_submitted">Proposal Submitted</Option>
              <Option value="confirmed_commit">Confirmed Commit</Option>
              <Option value="closed_won">Closed Won</Option>
              <Option value="closed_lost">Closed Lost</Option>
              <Option value="closed_dropped">Closed Dropped</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="expectedCloseDate"
            label="Expected Close Date"
            rules={[
              { required: true, message: "Please select expected close date" },
            ]}
          >
            <DatePicker
              className="w-full"
              placeholder="Select expected close date"
            />
          </Form.Item>
          <Form.Item name="probability" label="Win Probability (%)">
            <InputNumber
              min={0}
              max={100}
              className="w-full"
              placeholder="Enter win probability"
            />
          </Form.Item>
          <Form.Item name="notes" label="Conversion Notes">
            <TextArea
              rows={3}
              placeholder="Add any notes about this conversion..."
            />
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setConvertToDealModalVisible(false);
                  convertForm.resetFields();
                }}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={isLoading}>
                Convert to Deal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Task Modal */}
      <Modal
        title={editingTask ? "Edit Task" : "Add Task"}
        open={taskModalVisible}
        onCancel={() => {
          setTaskModalVisible(false);
          taskForm.resetFields();
          setEditingTask(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={taskForm} layout="vertical" onFinish={handleSaveTask}>
          <Form.Item
            name="title"
            label="Title"
            rules={[
              { required: true, message: "Please enter task title" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(
                      new Error("No leading or trailing spaces allowed")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Enter task title" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter task description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="priority" label="Priority" initialValue="medium">
                <Select>
                  <Select.Option value="low">Low</Select.Option>
                  <Select.Option value="medium">Medium</Select.Option>
                  <Select.Option value="high">High</Select.Option>
                  <Select.Option value="urgent">Urgent</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status" initialValue="pending">
                <Select>
                  <Select.Option value="pending">Pending</Select.Option>
                  <Select.Option value="in_progress">In Progress</Select.Option>
                  <Select.Option value="completed">Completed</Select.Option>
                  <Select.Option value="cancelled">Cancelled</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="dueDate"
            label="Due Date"
            extra="If not selected, defaults to tomorrow"
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="assignedTo" label="Assign To Team Member">
            <Select
              placeholder="Select team member"
              allowClear
              loading={loadingTeamMembers}
              showSearch
              filterOption={(input, option) => {
                const member = teamMembers.find(m => m.id === option.value);
                if (!member) return false;
                const searchText = input.toLowerCase();
                return (
                  member.name.toLowerCase().includes(searchText) ||
                  member.role?.toLowerCase().includes(searchText)
                );
              }}
            >
              {teamMembers.map((member) => (
                <Select.Option key={member.id} value={member.id}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Avatar size="small" src={member.avatar}>
                      {member.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <span>{member.name}</span>
                    <span style={{ color: "#999", fontSize: "12px" }}>
                      ({member.role})
                    </span>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Additional notes" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button
                onClick={() => {
                  setTaskModalVisible(false);
                  taskForm.resetFields();
                  setEditingTask(null);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingTask ? "Update" : "Create"} Task
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign Lead Modal */}
      <Modal
        title="Change Assignment"
        open={assignLeadModalVisible}
        onCancel={() => {
          setAssignLeadModalVisible(false);
          assignForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={assignForm}
          onFinish={handleAssignLead}
          layout="vertical"
          initialValues={{
            assignedTo: currentLead?.assignedTo?._id || currentLead?.assignedTo,
          }}
        >
          <Form.Item
            name="assignedTo"
            label="Assign to Team Member"
            rules={[{ required: true, message: "Please select a team member" }]}
          >
            <Select
              placeholder="Select team member"
              loading={loadingTeamMembers}
              showSearch
              filterOption={(input, option) => {
                const member = teamMembers.find(m => m.id === option.value);
                if (!member) return false;
                const searchText = input.toLowerCase();
                return (
                  member.name.toLowerCase().includes(searchText) ||
                  member.role?.toLowerCase().includes(searchText)
                );
              }}
            >
              {teamMembers.map((member) => (
                <Option key={member.id} value={member.id}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Avatar size="small" src={member.avatar}>
                      {member.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <span>{member.name}</span>
                    <span style={{ color: "#999", fontSize: "12px" }}>
                      ({member.role})
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setAssignLeadModalVisible(false);
                  assignForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update Assignment
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      



      {/* Proposal Generation Drawer */}
      <Drawer
        title="Create Proposal"
        placement="right"
        width={600}
        onClose={() => {
          setProposalDrawerVisible(false);
          proposalForm.resetFields();
        }}
        open={proposalDrawerVisible}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setProposalDrawerVisible(false);
                  proposalForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => proposalForm.submit()}
                loading={isGeneratingProposal}
                icon={<FilePdfOutlined />}
              >
                Generate Proposal
              </Button>
            </Space>
          </div>
        }
      >
        <Form
          form={proposalForm}
          layout="vertical"
          onFinish={handleGenerateProposal}
          initialValues={{
            proposalTitle: `Business Proposal for ${currentLead?.company || 'Client'}`,
            proposalType: 'business',
            validityDays: 30,
            includeCompanyProfile: true
          }}
        >
          <Form.Item
            name="proposalTitle"
            label="Proposal Title"
            rules={[{ required: true, message: 'Please enter proposal title' }]}
          >
            <Input placeholder="Enter proposal title" />
          </Form.Item>

          <Form.Item
            name="proposalType"
            label="Proposal Type"
            rules={[{ required: true, message: 'Please select proposal type' }]}
          >
            <Select placeholder="Select proposal type">
              <Option value="business">Business Proposal</Option>
              <Option value="project">Project Proposal</Option>
              <Option value="service">Service Proposal</Option>
              <Option value="product">Product Proposal</Option>
              <Option value="partnership">Partnership Proposal</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="validityDays"
            label="Validity Period (Days)"
            rules={[{ required: true, message: 'Please enter validity period' }]}
          >
            <InputNumber
              min={1}
              max={365}
              style={{ width: '100%' }}
              placeholder="Enter validity in days"
            />
          </Form.Item>

          <Form.Item
            name="estimatedValue"
            label={`Estimated Value (${currencySymbol})`}
            rules={[{ required: true, message: 'Please enter estimated value' }]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder="Enter estimated value"
              formatter={value => `${currencySymbol} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/₹\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="deliveryTimeline"
            label="Delivery Timeline"
            rules={[{ required: true, message: 'Please enter delivery timeline' }]}
          >
            <Input placeholder="e.g., 4-6 weeks, 3 months" />
          </Form.Item>

          <Form.Item
            name="keyServices"
            label="Key Services/Products"
            rules={[{ required: true, message: 'Please enter key services' }]}
          >
            <TextArea
              rows={4}
              placeholder="Enter key services or products to be included in the proposal (one per line)"
            />
          </Form.Item>

          <Form.Item
            name="specialTerms"
            label="Special Terms & Conditions"
          >
            <TextArea
              rows={3}
              placeholder="Enter any special terms, conditions, or notes (optional)"
            />
          </Form.Item>

          <Form.Item
            name="includeCompanyProfile"
            label="Include Company Profile"
          >
            <Select defaultValue={true}>
              <Option value={true}>Yes</Option>
              <Option value={false}>No</Option>
            </Select>
          </Form.Item>

          <div style={{ 
            background: '#f0f2f5', 
            padding: '12px', 
            borderRadius: '4px',
            marginTop: '16px'
          }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <strong>Note:</strong> The AI will automatically include lead details, requirements, 
              and create a comprehensive proposal based on the information provided above and 
              the lead data from the database.
            </Text>
          </div>
        </Form>
      </Drawer>

      {/* Proposal Preview Modal - 2 Column Layout */}
      <Modal
        title="Proposal Preview - Review & Edit"
        open={proposalPreviewVisible}
        onCancel={() => {
          setProposalPreviewVisible(false);
          setProposalDrawerVisible(true); // Reopen form drawer
        }}
        width="95%"
        style={{ top: 20, maxWidth: '1600px' }}
        footer={[
          <Button
            key="back"
            onClick={() => {
              setProposalPreviewVisible(false);
              setProposalDrawerVisible(true);
            }}
          >
            Back to Edit Form
          </Button>,
          <Button
            key="generate"
            type="primary"
            loading={isGeneratingProposal}
            onClick={handleConfirmProposal}
            icon={<FilePdfOutlined />}
          >
            Confirm & Generate PDF
          </Button>
        ]}
      >
        {proposalPreviewData && (
          <Row gutter={24}>
            {/* LEFT COLUMN - Company & Submitter Details */}
            <Col span={10}>
              <div className="space-y-4" style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', paddingRight: '12px' }}>
                {/* Company Information Section */}
                <Card 
                  title={
                    <Space>
                      <UserOutlined />
                      <span>Company Information (From)</span>
                    </Space>
                  }
                  size="small"
                  className="shadow-sm"
                >
                  <Form layout="vertical" size="small">
                    <Form.Item label="Company Name" style={{ marginBottom: '12px' }}>
                      <Input
                        value={editedCompanyInfo?.companyName}
                        onChange={(e) => setEditedCompanyInfo({...editedCompanyInfo, companyName: e.target.value})}
                        placeholder="Enter company name"
                      />
                    </Form.Item>
                    <Form.Item label="Contact Person" style={{ marginBottom: '12px' }}>
                      <Input
                        value={editedCompanyInfo?.yourName}
                        onChange={(e) => setEditedCompanyInfo({...editedCompanyInfo, yourName: e.target.value})}
                        placeholder="Enter your name"
                      />
                    </Form.Item>
                    <Form.Item label="Email" style={{ marginBottom: '12px' }}>
                      <Input
                        value={editedCompanyInfo?.email}
                        onChange={(e) => setEditedCompanyInfo({...editedCompanyInfo, email: e.target.value})}
                        placeholder="Enter email"
                      />
                    </Form.Item>
                    <Form.Item label="Phone" style={{ marginBottom: '12px' }}>
                      <Input
                        value={editedCompanyInfo?.phone}
                        onChange={(e) => setEditedCompanyInfo({...editedCompanyInfo, phone: e.target.value})}
                        placeholder="Enter phone"
                      />
                    </Form.Item>
                    <Form.Item label="Address" style={{ marginBottom: '12px' }}>
                      <Input
                        value={editedCompanyInfo?.address}
                        onChange={(e) => setEditedCompanyInfo({...editedCompanyInfo, address: e.target.value})}
                        placeholder="Enter address"
                      />
                    </Form.Item>
                    <Row gutter={8}>
                      <Col span={12}>
                        <Form.Item label="City" style={{ marginBottom: '12px' }}>
                          <Input
                            value={editedCompanyInfo?.city}
                            onChange={(e) => setEditedCompanyInfo({...editedCompanyInfo, city: e.target.value})}
                            placeholder="City"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="State" style={{ marginBottom: '12px' }}>
                          <Input
                            value={editedCompanyInfo?.state}
                            onChange={(e) => setEditedCompanyInfo({...editedCompanyInfo, state: e.target.value})}
                            placeholder="State"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item label="ZIP Code" style={{ marginBottom: '0' }}>
                      <Input
                        value={editedCompanyInfo?.zipCode}
                        onChange={(e) => setEditedCompanyInfo({...editedCompanyInfo, zipCode: e.target.value})}
                        placeholder="ZIP Code"
                      />
                    </Form.Item>
                  </Form>
                </Card>

                {/* Client Information Section */}
                <Card 
                  title={
                    <Space>
                      <MailOutlined />
                      <span>Prepared For (Client)</span>
                    </Space>
                  }
                  size="small"
                  className="shadow-sm"
                >
                  <Descriptions column={1} size="small" bordered>
                    <Descriptions.Item label="Company">{proposalPreviewData.leadInfo?.company}</Descriptions.Item>
                    <Descriptions.Item label="Contact Person">{proposalPreviewData.leadInfo?.contactPerson}</Descriptions.Item>
                    <Descriptions.Item label="Email">{proposalPreviewData.leadInfo?.email}</Descriptions.Item>
                    <Descriptions.Item label="Phone">{proposalPreviewData.leadInfo?.phone}</Descriptions.Item>
                  </Descriptions>
                </Card>

                {/* Proposal Details Section */}
                <Card 
                  title={
                    <Space>
                      <CalendarOutlined />
                      <span>Proposal Details</span>
                    </Space>
                  }
                  size="small"
                  className="shadow-sm"
                >
                  <Descriptions column={1} size="small" bordered>
                    <Descriptions.Item label="Title">{proposalPreviewData.proposalData?.proposalTitle}</Descriptions.Item>
                    <Descriptions.Item label="Validity">{proposalPreviewData.proposalData?.validityDays} days</Descriptions.Item>
                    <Descriptions.Item label="Estimated Value">
                      <Text strong style={{ color: '#52c41a' }}>
                        {formatCurrency(proposalPreviewData.proposalData?.estimatedValue)}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Delivery Timeline">{proposalPreviewData.proposalData?.deliveryTimeline}</Descriptions.Item>
                  </Descriptions>
                </Card>
              </div>
            </Col>

            {/* RIGHT COLUMN - Proposal Draft */}
            <Col span={14}>
              <Card 
                title={
                  <Space>
                    <FileTextOutlined />
                    <span>Proposal Draft (Editable)</span>
                  </Space>
                }
                size="small"
                className="shadow-sm"
                style={{ height: 'calc(100vh - 250px)' }}
                styles={{ body: { height: 'calc(100% - 45px)', padding: '12px' } }}
              >
                <TextArea
                  value={editedProposalContent}
                  onChange={(e) => setEditedProposalContent(e.target.value)}
                  style={{ 
                    height: 'calc(100% - 50px)',
                    fontFamily: 'monospace', 
                    fontSize: '13px',
                    resize: 'none'
                  }}
                  placeholder="AI-generated proposal content will appear here..."
                />
                <div style={{ 
                  marginTop: '12px', 
                  padding: '8px 12px', 
                  background: '#e6f7ff', 
                  border: '1px solid #91d5ff',
                  borderRadius: '4px' 
                }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    <strong>💡 Tip:</strong> Edit the proposal content above. All changes will be reflected in the final PDF.
                  </Text>
                </div>
              </Card>
            </Col>
          </Row>
        )}
      </Modal>

      {/* Phone Selection Modal */}
      <Modal
        title="Select Phone Number for Call"
        open={phoneSelectionModalVisible}
        onCancel={() => setPhoneSelectionModalVisible(false)}
        onOk={handleConfirmPhoneSelection}
        okText="Make Call"
        cancelText="Cancel"
        width={600}
      >
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Select Country:
          </label>
          <Select
            value={selectedCountryIndex}
            onChange={(value) => {
              setSelectedCountryIndex(value);
              setSelectedPhoneIndex(0); // Reset phone selection
            }}
            style={{ width: '100%' }}
          >
            {callSettings?.plivoSettings?.countries?.map((country, index) => (
              <Option key={index} value={index}>
                {country.countryName} ({country.countryCode})
              </Option>
            ))}
          </Select>
        </div>

        {callSettings?.plivoSettings?.countries?.[selectedCountryIndex] && (
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Select Phone Number:
            </label>
            <Select
              value={selectedPhoneIndex}
              onChange={setSelectedPhoneIndex}
              style={{ width: '100%' }}
            >
              {callSettings.plivoSettings.countries[selectedCountryIndex].phoneNumbers?.map((phone, index) => (
                <Option key={index} value={index}>
                  {phone}
                </Option>
              ))}
            </Select>

            {/* Display selected country details */}
            <Card
              style={{ marginTop: '16px', backgroundColor: '#f5f5f5' }}
              size="small"
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Country">
                  {callSettings.plivoSettings.countries[selectedCountryIndex].countryName}
                </Descriptions.Item>
                <Descriptions.Item label="Country Code">
                  {callSettings.plivoSettings.countries[selectedCountryIndex].countryCode}
                </Descriptions.Item>
                <Descriptions.Item label="Selected Phone">
                  {callSettings.plivoSettings.countries[selectedCountryIndex].phoneNumbers[selectedPhoneIndex]}
                </Descriptions.Item>
                {/* <Descriptions.Item label="Provider">
                  Plivo
                </Descriptions.Item> */}
              </Descriptions>
            </Card>
          </div>
        )}
      </Modal>

      {/* AI Call Phone Selection Modal */}
      <Modal
        title="Select Phone Number for AI Call"
        open={aiCallPhoneModalVisible}
        onCancel={() => setAICallPhoneModalVisible(false)}
        onOk={handleConfirmAICallPhoneSelection}
        okText="Start AI Call"
        cancelText="Cancel"
        width={600}
        confirmLoading={isInitiatingAICall}
      >
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Select Country:
          </label>
          <Select
            value={aiCallSelectedCountryIndex}
            onChange={(value) => {
              setAICallSelectedCountryIndex(value);
              setAICallSelectedPhoneIndex(0); // Reset phone selection
            }}
            style={{ width: '100%' }}
          >
            {callSettings?.plivoSettings?.countries?.map((country, index) => (
              <Option key={index} value={index}>
                {country.countryName} ({country.countryCode})
              </Option>
            ))}
          </Select>
        </div>

        {callSettings?.plivoSettings?.countries?.[aiCallSelectedCountryIndex] && (
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Select Phone Number:
            </label>
            <Select
              value={aiCallSelectedPhoneIndex}
              onChange={setAICallSelectedPhoneIndex}
              style={{ width: '100%' }}
            >
              {callSettings.plivoSettings.countries[aiCallSelectedCountryIndex].phoneNumbers?.map((phone, index) => (
                <Option key={index} value={index}>
                  {phone}
                </Option>
              ))}
            </Select>

            {/* Display selected country details */}
            <Card
              style={{ marginTop: '16px', backgroundColor: '#f5f5f5' }}
              size="small"
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Country">
                  {callSettings.plivoSettings.countries[aiCallSelectedCountryIndex].countryName}
                </Descriptions.Item>
                <Descriptions.Item label="Country Code">
                  {callSettings.plivoSettings.countries[aiCallSelectedCountryIndex].countryCode}
                </Descriptions.Item>
                <Descriptions.Item label="Selected Phone">
                  {callSettings.plivoSettings.countries[aiCallSelectedCountryIndex].phoneNumbers[aiCallSelectedPhoneIndex]}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LeadDetailView;
